import React from "react";
import {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import AuthContext from "../../contexts/AuthProvider";
import useThemeColors from "../../hooks/useThemeColors";
import usePermissions from "../../hooks/usePermissions";

import { Grid as GridSpinner } from "react-loader-spinner";
import moment from "moment/moment";
import cx from "classnames";
import { toast, Slide as ToastSlide } from "react-toastify";
import { client } from "../../services/axiosClient";
import {
  fetchInputsStateV3,
  downloadInputV3,
  fetchInputsV3PendingSummary,
  markInputNoteVerifiedV3,
} from "../../services/inputsV3Service";
import fileDownload from "js-file-download";
import { adaptSnapshotToV2Shape } from "../../services/inputsV3Adapter";
import {
  classifyInputAggregate,
  hasAttentionSignal,
  isInputDelivered,
} from "../../services/inputsV3StateRules";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";

// material-ui icons
import { Assignment, Close, Search } from "@material-ui/icons";

// core components
import GridContainer from "../../components/Grid/GridContainer.js";
import GridItem from "../../components/Grid/GridItem.js";
import Card from "../../components/Card/Card.js";
import CardBody from "../../components/Card/CardBody.js";
import CardIcon from "../../components/Card/CardIcon.js";
import CardHeader from "../../components/Card/CardHeader.js";
import Button from "../../components/CustomButtons/Button";
import FullPageLoader from "../../components/Loaders/FullPageLoader";
import SnackbarContent from "../../components/Snackbar/SnackbarContent";

import {
  blackColor,
  grayColor,
  whiteColor,
  blendColors,
} from "../../assets/jss/material-dashboard-pro-react";
import {
  inputsStyles,
  Transition,
  ResetInputsDialog,
  InputNotesDialog,
} from "./inputs_components";
import { useUploadQueue } from "./inputs_components/hooks/useUploadQueue";
import { useSpecialOptionsModal } from "./inputs_components/hooks/useSpecialOptionsModal";
import UploadQueueDrawer from "./inputs_components/UploadQueueDrawer";
import SpecialOptionsModal from "./inputs_components/SpecialOptionsModal";
import {
  InputBoxV3,
  InputGroupListV3,
  InputDrawerV3,
  InputsV3GlobalTopBar,
  MonthSelectorV3,
  ModelingProgressBandV3,
  OverviewV3,
  ProgressDrawerV3,
  DataPrivacyModal,
  HowToModalV3,
} from "./inputs_v3_components";
import useProgressOverviewV3 from "./inputs_v3_components/progress_drawer/useProgressOverviewV3";
import useInputsStateV3, {
  shapeDrawerOverrideFromState,
} from "./inputs_v3_components/useInputsStateV3";
import { useInputUpload } from "./inputs_v3_components/hooks/useInputUpload";

/** InSights Demo implementation — must match `Implementation.implementation_name` in Django. */
const INSIGHTS_DEMO_IMPLEMENTATION_NAME = "Profit InSights";

/** Abreviações alinhadas a ``common.months.<abbr>`` (rótulo da faixa de progresso). */
const MONTH_PROGRESS_LABEL_ABBR = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

export default function InputsV3() {
  // region initial declarations
  let { userDetails } = useContext(AuthContext);
  const useStyles = makeStyles(inputsStyles);
  const classes = useStyles();
  const { clientColor } = useThemeColors();
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  // Gate do drag & drop de arquivos (mesmo atomo do botao "Enviar
  // arquivo" do drawer / endpoint ``@requires('upload')``). Sem ele os
  // cards e o drawer NAO viram alvos droppable.
  const canUploadInputs = can("modeling.inputs.upload");
  // Progresso do multipart upload (0..99 durante; reseta p/ 0 ao fim).
  // Alimenta o overlay de envio na barra fixa superior.
  const uploadProgressValue = useSelector(
    (s) => s.file_transfer?.progress || 0,
  );

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "0",
      borderWidth: "0 0 1px 0",
      borderColor: state.isFocused ? clientColor : grayColor[7],
      marginTop: "-12px",
      boxShadow: "none",
      "&:hover": {
        borderColor: clientColor,
      },
    }),
    option: (provided, state) => ({
      ...provided,
      paddingTop: "3px",
      paddingBottom: "3px",
      backgroundColor: state.isSelected
        ? blendColors(whiteColor, clientColor, 0.8)
        : state.isFocused
          ? blendColors(whiteColor, clientColor, 0.9)
          : whiteColor,
      color: blackColor,
      "&:active": {
        backgroundColor: blendColors(whiteColor, clientColor, 0.85),
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: blackColor,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: blendColors(whiteColor, clientColor, 0.85),
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: blackColor,
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: grayColor[0],
      "&:hover": {
        backgroundColor: blendColors(whiteColor, clientColor, 0.7),
        color: blackColor,
      },
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: "2px",
      zIndex: "9999",
    }),
    // Quando o select usa `menuPortalTarget={document.body}`, o menu é
    // reparentado pra fora da árvore local e precisa de z-index no portal
    // (não só no menu) pra não ficar atrás de elementos stacked abaixo
    // dele — ex.: a tabela sticky do OverviewV3.
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? clientColor : grayColor[7],
      "&:hover": { color: clientColor },
    }),
  };
  // endregion

  // region toasts
  const notify = (text, type = "info", time = 3000) =>
    toast(text, {
      position: "top-right",
      autoClose: time,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: "light",
      type: type,
      transition: ToastSlide,
    });

  const notifyCopy = () =>
    toast(t("tasks.values_copied"), {
      position: "bottom-center",
      autoClose: 1200,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      theme: "light",
      type: "info",
      transition: ToastSlide,
    });
  // endregion

  // Navegacao vinda do sino de "Decisoes pendentes" (floating
  // toolbar): quando o usuario clica no sino, DecisionsToolbarItem
  // navega para ``/main/inputs_v3`` passando ``state.focusPendingDecisions``.
  // Usamos esse sinal uma unica vez, apos o snapshot carregar, para
  // abrir automaticamente o MES MAIS ANTIGO que tem decisao pendente
  // do usuario — em vez do default de abrir o mes mais recente.
  const location = useLocation();
  const focusPendingDecisionsRef = useRef(
    !!location.state?.focusPendingDecisions,
  );

  // region state declarations
  const [pipelineInProgress, setPipelineInProgress] = useState(false);
  const [monthlyStatus, setMonthlyStatus] = useImmer([]);
  // Estado vivo do mes carregado pelo endpoint unico. O status visual
  // do seletor/badge vem de ``month_aggregates``; aqui mantemos apenas
  // os atores ativos do mes selecionado para a descricao contextual.
  const [selectedMonthCanonical, setSelectedMonthCanonical] = useState(null);
  // Set<"YYYY-MM"> — meses que tem decisao pendente (InputDecisionV3)
  // para a implementacao ativa. Serve para (1) marcar os meses com
  // ponto no MonthSelectorV3 e (2) calcular o mes mais antigo com
  // pendencia quando chega o sinal do sino.
  const [pendingDecisionsByMonth, setPendingDecisionsByMonth] = useState(
    () => new Set(),
  );
  const usersRef = useRef([]);
  const [filteredResponsibleUsers, setFilteredResponsibleUsers] = useState([]);
  // Indicador de "salvando responsaveis" exposto ao InputDrawerV3 →
  // ResponsiblesPane. Espelha exatamente o booleano homonimo do V2
  // (``isSavingInputResponsible``) — mesma UX, mesmo endpoint.
  const [isSavingResponsibles, setIsSavingResponsibles] = useState(false);

  const [currentView, setCurrentView] = useState("detailed");
  const [dataPrivacyModalOpen, setDataPrivacyModalOpen] = useState(false);
  const [howToModalOpen, setHowToModalOpen] = useState(false);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  // Aba inicial do ``ProgressDrawerV3`` quando ele e aberto via
  // ``InputsV3GlobalTopBar`` (tiles "Pendências" / "Blocos"). O
  // contrato historico (botao "Cálculos" da ``ModelingProgressBandV3``,
  // sino de pendencias do header global) continua passando por
  // ``setProgressModalOpen(true)`` direto, e a aba default volta
  // para ``blocks``.
  const [progressInitialTab, setProgressInitialTab] = useState("blocks");
  const openProgressDrawerOnTab = useCallback((tab) => {
    setProgressInitialTab(tab || "blocks");
    setProgressModalOpen(true);
  }, []);
  const [maintenanceStatus, setMaintenanceStatus] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedMonthData, setSelectedMonthData] = useState(null);
  const [inputsStatus, setInputsStatus] = useImmer([]);
  const [inputDrawerInput, setInputDrawerInput] = useImmer(null);

  // ProgressOverview consolidado (cockpit + timeline + pendencias +
  // status do mes para o badge). Polling 10s. Habilitado assim que
  // ``selectedMonthData`` esta disponivel.
  const progressOverview = useProgressOverviewV3({
    year: selectedMonthData?.year,
    month: selectedMonthData?.month,
    enabled: !!selectedMonthData,
  });

  // Onda 2.0 (mai/2026): hook UNICO de polling que consolida o trio
  // ``snapshot``/``blocks``/``drawer-details`` num so endpoint
  // (``/api/v3/inputs/state/``). Eliminado: o polling proprio do
  // ``BlocksPane`` (``useBlocksV3``) e o do ``InputDrawerV3``
  // (``useDrawerDetailsV3``) — passam a receber o payload via prop
  // override (``blocksOverride`` / ``drawerDetailsOverride``).
  //
  // O ``refreshInputsStatus`` continua existindo para o ``inputsStatus``
  // ate' a proxima sub-onda, quando vamos consolidar o consumo do
  // ``data.runtime`` aqui dentro tambem (eliminando o polling
  // duplicado para o mesmo endpoint). Custo intermediario: durante
  // esta janela o cliente faz 2 chamadas ao ``state/`` (uma do
  // ``refreshInputsStatus`` autopoll, outra do ``useInputsStateV3``)
  // — o ETag corta no servidor, mas vale o cleanup.
  const inputDrawerMetaName = inputDrawerInput?.meta_name || null;
  const inputsState = useInputsStateV3({
    year: selectedMonthData?.year,
    month: selectedMonthData?.month,
    drawerMeta: inputDrawerInput !== null ? inputDrawerMetaName : null,
    enabled: !!selectedMonthData,
  });
  const liveSnapshot = useMemo(() => {
    const payload = inputsState.data?.runtime;
    if (!payload) return null;
    const period = payload?.period;
    if (
      period &&
      selectedMonthData &&
      (period.year !== selectedMonthData.year ||
        period.month !== selectedMonthData.month)
    ) {
      return null;
    }
    return {
      payload,
      period,
      adapted: adaptSnapshotToV2Shape(payload),
    };
  }, [inputsState.data, selectedMonthData]);
  const currentInputsStatus =
    liveSnapshot?.adapted?.inputsStatus || inputsStatus;

  // Auto-cura do filtro "status por responsável": a caixa só renderiza
  // um chip por responsável (ou "Sem responsável") que tenha >=1 input
  // nos dados atuais. Se o grupo filtrado esvazia (ex.: defini
  // responsáveis em TODOS os inputs antes "sem responsável", ou revoguei
  // um responsável de todos os seus inputs), o chip some — mas o filtro
  // continuava ativo, deixando a lista 100% filtered-out e sem UI para
  // desmarcar. Aqui removemos do filtro qualquer id que não exista mais
  // entre os responsáveis disponíveis, restaurando a visibilidade.
  useEffect(() => {
    const available = new Set();
    let hasNoResponsible = false;
    for (const group of currentInputsStatus || []) {
      for (const inp of group.inputs || []) {
        const respList = Array.isArray(inp.responsible) ? inp.responsible : [];
        if (respList.length === 0) {
          hasNoResponsible = true;
        } else {
          respList.forEach((r) => {
            if (r && r.id != null) available.add(r.id);
          });
        }
      }
    }
    if (hasNoResponsible) available.add("__no_responsible__");
    setFilteredResponsibleUsers((prev) => {
      const pruned = prev.filter((u) => available.has(u.id));
      return pruned.length === prev.length ? prev : pruned;
    });
  }, [currentInputsStatus]);

  const drawerDetailsOverride = useMemo(() => {
    if (inputDrawerInput === null) return null;
    // Shape "loading" — usado quando:
    //   - ainda nao ha snapshot ativo (primeiro fetch do polling), OU
    //   - o snapshot existe mas ``drawer_details`` esta zerado porque
    //     o ``useInputsStateV3`` acabou de detectar mudanca de drawer
    //     e limpou a sub-secao stale (auditoria mai/2026 — CCU Testes:
    //     drawer renderizando conteudo do input anterior por ~200ms).
    // Devolver loading aqui mantem o ``overrideActive=true`` no
    // ``InputDrawerV3`` e evita que o ``useDrawerDetailsV3`` legado
    // dispare um fetch competindo com o consolidado.
    const loadingShape = {
      loading: true,
      about: null,
      control: null,
      currentMonthUploads: [],
      previousMonthUpload: null,
      notes: [],
      answeredDecisions: [],
      decisions: [],
      crossLinks: [],
      auditRecent: [],
      runtimeInput: null,
      monthStatus: null,
      error: null,
      refresh: inputsState.refresh || (() => {}),
    };
    if (!liveSnapshot) return loadingShape;
    const shaped = shapeDrawerOverrideFromState(
      inputsState.data,
      inputsState.refresh,
    );
    return shaped || loadingShape;
  }, [inputDrawerInput, liveSnapshot, inputsState.data, inputsState.refresh]);
  const currentInputDrawerInput = useMemo(() => {
    if (!inputDrawerInput) return null;
    const currentMeta = inputDrawerInput.meta_name || inputDrawerInput.input_id;
    for (const group of currentInputsStatus || []) {
      const found = (group.inputs || []).find(
        (input) =>
          input.input_id === inputDrawerInput.input_id ||
          input.meta_name === currentMeta,
      );
      if (found) return found;
    }
    return inputDrawerInput;
  }, [inputDrawerInput, currentInputsStatus]);

  // Onda 2.0 — passo D: ``inputsStatus``/``monthlyStatus`` e atores
  // ativos do mes agora sao DERIVADOS do payload do
  // hook unico (``inputsState.data.runtime``), nao mais do polling
  // proprio do ``refreshInputsStatus``. O auto-poll legado
  // (``setTimeout(refreshInputsStatus, 3000)``) foi removido —
  // ``useInputsStateV3`` cobre essa cadencia (4s vivo / 30s parado),
  // e o ``refreshInputsStatus`` continua existindo SO para refreshes
  // manuais disparados por handlers de upload/replicate/reprocess.
  // Esse useEffect aplica a mesma logica do ``.then`` antigo (adapter
  // + otimismo + monthlyStatus + canonical), preservando comportamento
  // exato — apenas trocando a fonte (hook em vez de fetch ad-hoc).
  const lastAppliedRevisionRef = useRef(0);

  useEffect(() => {
    if (!liveSnapshot) return;
    if (inputsState.revision === lastAppliedRevisionRef.current) return;
    const { payload, adapted } = liveSnapshot;
    const refreshedPeriod = payload?.period;
    lastAppliedRevisionRef.current = inputsState.revision;
    v3AppNameRef.current = adapted.implementation?.app_name || null;
    usersRef.current = adapted.users;
    setMaintenanceStatus(payload?.runtime?.maintenance || null);
    if (refreshedPeriod) {
      lastRefreshParams.current = JSON.stringify({
        month: refreshedPeriod.month,
        year: refreshedPeriod.year,
      });
    }
    checkIfAutoGeneratedInputsAreReady(adapted.inputsStatus);
    setInputsStatus(adapted.inputsStatus);
    if (
      Array.isArray(adapted.monthlyStatus) &&
      adapted.monthlyStatus.length > 0
    ) {
      setMonthlyStatus(adapted.monthlyStatus);
    }
    if (refreshedPeriod && adapted.selectedMonthStatus) {
      setSelectedMonthCanonical({
        year: refreshedPeriod.year,
        month: refreshedPeriod.month,
        status: adapted.selectedMonthStatus,
        reason: adapted.selectedMonthStatusReason || null,
        active: adapted.selectedMonthStatusActive || null,
      });
    }
    setIsRefreshFadeActive(false);
    setIsRefreshing(false);
  }, [liveSnapshot, inputsState.revision]);

  // ``monthlyStatus`` enriquecido com o ``status`` real do backend
  // (regra V3, leva 80). Usado pelo badge (OverviewV3),
  // ``MonthSelectorV3`` e ``ProgressDrawerV3``.
  //
  // Quando os agregados do backend nao chegaram ainda, devolvemos
  // ``monthlyStatus`` com ``status`` zerado (``null``) — o
  // ``MonthSelectorV3`` ja interpreta ``status=null`` como
  // "aguardando" (cinza neutro). ANTES caiamos no ``status`` do
  // adapter front-end (``deriveMonthStatus`` — so olha inputs); isso
  // gerava divergencia com o chip do mes selecionado, que usa o
  // backend completo (inputs + xlinks + blocks). O bug era visivel
  // como "selector defasado em relacao ao chip", e com criterios
  // diferentes nao tinha como convergir mesmo apos o load.
  const monthlyStatusV3 = useMemo(() => {
    const aggregates = progressOverview.data?.month_aggregates || [];
    const aggByPeriod = new Map(
      aggregates.map((a) => [
        `${a.year}-${a.month}`,
        {
          status: a.status,
          reason: a.reason || null,
          has_pending_input_decision: Boolean(a.has_pending_input_decision),
        },
      ]),
    );
    return monthlyStatus.map((m) => {
      const key = `${m.year}-${m.month}`;
      const agg = aggByPeriod.get(key);
      const status = agg?.status ?? null;
      const reason = agg?.reason ?? null;
      const hasPendingInputDecision = Boolean(agg?.has_pending_input_decision);
      return {
        ...m,
        status,
        reason,
        has_pending_input_decision: hasPendingInputDecision,
        active: null,
      };
    });
  }, [monthlyStatus, progressOverview.data]);

  // Status efetivo do mes selecionado, vindo do backend V3 (regra
  // unificada da leva 81). Mesma fonte que o ``MonthSelectorV3``
  // (``monthlyStatusV3``) — garantia de paridade entre o badge do
  // mes selecionado e o badge do mesmo mes no seletor.
  //
  // Antes caiamos em ``selectedMonthData.status`` quando o lookup
  // falhava. Esse status vem do adapter front-end
  // (``deriveMonthStatus``) que olha SO inputs — nao considera
  // xlinks nem blocks. Resultado: durante o intervalo entre o click
  // e o backend devolver, o chip mostrava ``complete`` enquanto o
  // selector ja mostrava ``stale``/``ready_to_calculate``. Bug
  // reportado como "selector defasado". Removido o fallback; o
  // hook ``useProgressOverviewV3`` agora preserva o ultimo ``data``
  // durante refetch, eliminando a janela em que o chip ficaria sem
  // status.
  const selectedMonthStatusV3 = useMemo(() => {
    if (liveSnapshot?.adapted?.selectedMonthStatus) {
      return liveSnapshot.adapted.selectedMonthStatus;
    }
    if (!selectedMonthData) return null;
    const match = monthlyStatusV3.find(
      (m) =>
        m.year === selectedMonthData.year &&
        m.month === selectedMonthData.month,
    );
    return match?.status ?? null;
  }, [liveSnapshot, monthlyStatusV3, selectedMonthData]);
  // Onda 1.7: ``reason`` (D-MTX-2) acompanha o ``status`` para
  // alimentar o label contextual do chip do mes
  // (``Processando cruzamentos`` vs ``Processando calculos``,
  // ``Aguardando inputs`` vs ``Sem inputs``).
  const selectedMonthStatusReasonV3 = useMemo(() => {
    if (liveSnapshot?.adapted) {
      return liveSnapshot.adapted.selectedMonthStatusReason ?? null;
    }
    if (!selectedMonthData) return null;
    const match = monthlyStatusV3.find(
      (m) =>
        m.year === selectedMonthData.year &&
        m.month === selectedMonthData.month,
    );
    return match?.reason ?? null;
  }, [liveSnapshot, monthlyStatusV3, selectedMonthData]);
  // Onda 1.8c: lista de atores em execucao no mes selecionado.
  // Alimenta texto contextual ao lado do badge ("Lendo Venta Bruta...",
  // "Cruzando Venta x Ajustes...", "Processando PnL Logistica...").
  const selectedMonthStatusActiveV3 = useMemo(() => {
    if (liveSnapshot?.adapted) {
      return liveSnapshot.adapted.selectedMonthStatusActive ?? null;
    }
    if (!selectedMonthData || !selectedMonthCanonical) return null;
    if (
      selectedMonthCanonical.year !== selectedMonthData.year ||
      selectedMonthCanonical.month !== selectedMonthData.month
    ) {
      return null;
    }
    return selectedMonthCanonical.active ?? null;
  }, [liveSnapshot, selectedMonthCanonical, selectedMonthData]);

  const modelingProgressBandMonthLabel = useMemo(() => {
    if (!selectedMonthData) return null;
    const name = t(
      `common.months.${MONTH_PROGRESS_LABEL_ABBR[selectedMonthData.month - 1]}`,
    );
    return `${name} ${selectedMonthData.year}`;
  }, [selectedMonthData, t]);

  const [isRefreshing, setIsRefreshing] = useState(true);
  const [isRefreshFadeActive, setIsRefreshFadeActive] = useState(false);
  const controller = useRef(null);
  const autoRefreshTimeout = useRef(null);
  const lastRefreshParams = useRef("");
  // ``initialPeriodRef`` guarda o ``period`` que o backend devolveu no
  // mount. O useEffect que escolhe ``selectedMonth`` padrao usa esse
  // ref para alinhar com o mes ja carregado — evita duplo snapshot
  // fetch no load (mount buscava X, efeito setava Y, refresh buscava
  // Y de novo). Ver nota em buildMonthlyStatusWindow (adapter).
  const initialPeriodRef = useRef(null);
  // V3: ``app_name`` da implementação ativa, preenchido a partir do snapshot.
  // O endpoint de upload V3 é path-based (``/api/v3/inputs/<app_name>/...``),
  // então precisamos dessa info fora do render. Usamos ``useRef`` para não
  // causar re-render ao atualizar.
  const v3AppNameRef = useRef(null);
  // V3 only: filtro ativo ao clicar em um dos badges (criticos / em atencao)
  // dos cards superiores. Estrutura: { scope: "all"|"mine", type: "critical"|"attention" }.
  const [badgeFilter, setBadgeFilter] = useState(null);
  // V3 only: modo de ordenação da lista de inputs.
  //   "group"       — respeita os grupos do snapshot (InputGroupV3, D-20).
  //   "attention"   — achata e ordena por severidade (crítico → não enviado).
  //   "responsible" — achata e agrupa virtualmente por responsável, ordenando
  //                   dentro de cada bloco por severidade. Inputs sem
  //                   responsável caem em uma seção virtual ao final.
  // Session-scoped (sem persistência por usuário) — reabrir decisão quando
  // houver dor real (P-D no README).
  const [sortMode, setSortMode] = useState("group");

  // V3 only: ids dos grupos atualmente abertos no card-accordion
  // (``InputGroupListV3``). Usuario pediu (2026-04-24) que:
  //   - O estado padrao seja "todos fechados".
  //   - Seja possivel fechar todos (nao forcar que um fique aberto).
  //   - Seja possivel abrir varios ao mesmo tempo.
  // ``Set<string>`` para lookup O(1). Imutavel a cada toggle.
  const [openGroupIds, setOpenGroupIds] = useState(() => new Set());
  const toggleGroup = useCallback((id) => {
    setOpenGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // V3 only: filtro textual por nome do input. Apenas client-side —
  // trabalhamos com a pequena lista ja hidratada pelo snapshot. Busca
  // case/diacritics-insensitive contra o ``display_name`` localizado e
  // o ``meta_name`` como rede de seguranca (quando o usuario busca pela
  // chave tecnica).
  const [nameFilter, setNameFilter] = useState("");

  // PA-019: as antigas medicoes de altura para sincronizar os 3 cards
  // do topo (counterCardHeight + ResizeObserver) e o
  // ``responsibleScrollRef`` que vivia aqui foram removidos junto com
  // a migracao do conteudo para ``ModelingProgressBandV3``. O novo
  // componente gerencia internamente seu proprio scroll de
  // responsaveis (via ``useOverlayScrollStyles``) e nao precisa de
  // altura imposta de fora.

  const [displayNotesModalOpen, setDisplayNotesModalOpen] = useState(false);
  const [resetInputsModalOpen, setResetInputsModalOpen] = useState(false);

  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [, setWrongDataType] = useState(false);
  // ``isUploading`` agora vive no hook ``useInputUpload`` (Onda 1.2).
  // Mantemos o destructuring abaixo para nao precisar mexer nos
  // consumidores (handleMonthSelectionChange etc.).

  const [
    inputControlsMarkedAsVerifiedLine,
    setInputControlsMarkedAsVerifiedLine,
  ] = useImmer([]);

  // endregion

  // region useEffect's
  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);
  useEffect(() => {
    // V3: s\u00f3 inicializamos selectedMonth/selectedMonthData depois que o
    // snapshot chega (monthlyStatus com pelo menos 1 item). No V2 isso
    // funcionava mesmo disparando cedo porque sempre havia v\u00e1rios meses
    // (ent\u00e3o `setSelectedMonth(length-1)` mudava o valor duas vezes);
    // com o snapshot V3 tendo 1 mes, setar cedo para 0 impedia o
    // useEffect [selectedMonth] de disparar quando os dados chegavam.
    //
    // Dois caminhos de inicializacao:
    //   (A) Navegacao padrao -> abre o mes MAIS RECENTE (ultimo da
    //       lista). Comportamento herdado do V2.
    //   (B) Navegacao vinda do sino de decisoes pendentes
    //       (``focusPendingDecisionsRef.current === true``) -> abre o
    //       mes MAIS ANTIGO que tem decisao pendente do usuario. Se
    //       nao ha pendencia (ou o fetch de decisoes ainda nao voltou
    //       e demorou mais que o snapshot), cai no caminho (A) como
    //       fallback seguro — nunca deixa a tela sem mes selecionado.
    if (selectedMonthData === null && monthlyStatus.length > 0) {
      // Alvo default: o mes que o backend devolveu como ``period``
      // (``initialPeriodRef``). Antes usavamos ``length - 1``, que
      // funcionava quando a lista tinha 1 item; com a janela completa
      // (jan-2024 ... abr-2026 para ccu_testes) isso jogava o usuario
      // num mes em branco no futuro e forcava um segundo fetch. Agora
      // casamos com ``period`` para que o mount nao precise refazer o
      // snapshot — o dado ja esta carregado.
      const p = initialPeriodRef.current;
      let targetIdx = monthlyStatus.length - 1;
      if (p) {
        const matched = monthlyStatus.findIndex(
          (m) => m.year === p.year && m.month === p.month,
        );
        if (matched !== -1) targetIdx = matched;
      }
      if (focusPendingDecisionsRef.current && pendingDecisionsByMonth.size) {
        for (let i = 0; i < monthlyStatus.length; i++) {
          const m = monthlyStatus[i];
          const key = `${m.year}-${String(m.month).padStart(2, "0")}`;
          if (pendingDecisionsByMonth.has(key)) {
            targetIdx = i;
            break;
          }
        }
        // Consumimos o sinal — proximas atualizacoes de
        // pendingDecisionsByMonth nao devem fazer o mes pular
        // sozinho (o usuario ja pode ter trocado manualmente).
        focusPendingDecisionsRef.current = false;
      }
      setSelectedMonth(targetIdx);
      setSelectedMonthData(monthlyStatus[targetIdx]);
    }
  }, [monthlyStatus, pendingDecisionsByMonth]);

  // Carrega ``InputDecisionV3`` pendentes da implementacao ativa. Usado
  // para dois propositos: (1) marcar visualmente os meses com ponto
  // laranja no MonthSelectorV3; (2) permitir a auto-abertura do mes
  // mais antigo com pendencia quando o usuario chega via sino.
  //
  // Degradacao silenciosa em caso de erro: mantemos o Set vazio. Se
  // a implementacao nao esta na allowlist V3 (403), o endpoint volta
  // vazio e simplesmente nao ha marcadores — a pagina continua
  // funcional. Idem para timeouts/falhas de rede.
  useEffect(() => {
    // Consome ``/api/v3/pending-summary/`` — agregado cross-periodo que
    // ja une decisoes pendentes + inputs nao enviados. O sino global
    // usa o mesmo endpoint (consistencia entre o badge do header e os
    // marcadores de mes no seletor).
    // Onda 1.1: AbortController + cancelled flag (defesa em
    // profundidade). Sem o signal, o axios continuava o request mesmo
    // depois do unmount; agora cancelamos a request real.
    const summaryController = new AbortController();
    let cancelled = false;
    fetchInputsV3PendingSummary({ signal: summaryController.signal })
      .then((data) => {
        if (cancelled) return;
        const keys = Array.isArray(data?.months_with_pending)
          ? data.months_with_pending
          : [];
        setPendingDecisionsByMonth(new Set(keys));
      })
      .catch((error) => {
        if (cancelled) return;
        if (error?.name === "CanceledError") return;
        setPendingDecisionsByMonth(new Set());
      });
    return () => {
      cancelled = true;
      summaryController.abort();
    };
  }, []);
  useEffect(() => {
    const pipeline = isPipelineInProgress();
    if (pipeline !== pipelineInProgress) setPipelineInProgress(pipeline);
  }, [currentInputsStatus, selectedMonthData]);

  useEffect(() => {
    const onMaintenanceChanged = (event) => {
      setMaintenanceStatus(event?.detail || null);
    };
    window.addEventListener("inputsV3MaintenanceChanged", onMaintenanceChanged);
    return () => {
      window.removeEventListener(
        "inputsV3MaintenanceChanged",
        onMaintenanceChanged,
      );
    };
  }, []);
  // I-8 (auditoria mai/2026): cleanup do ``autoRefreshTimeout`` no
  // unmount. Antes, se o usu\u00e1rio sa\u00edsse da view durante um pipeline em
  // andamento, o ``setTimeout(refreshInputsStatus, 3000)`` continuava
  // disparando \u2014 fetches contra um componente desmontado, console
  // ru\u00eddo, e potencialmente uma "memory leak" sutil.
  //
  // Onda 1.1: tambem abortamos qualquer ``fetchInputsV3Snapshot`` em
  // voo (via ``controller.current.abort()``) no unmount.
  useEffect(() => {
    return () => {
      if (autoRefreshTimeout.current) {
        clearTimeout(autoRefreshTimeout.current);
        autoRefreshTimeout.current = null;
      }
      if (controller.current) {
        try {
          controller.current.abort();
        } catch (_) {
          // ignore - controller ja abortado
        }
        controller.current = null;
      }
    };
  }, []);

  // ``mountedRef``: usado pelos callbacks de fetch (mount inicial,
  // refresh, pending summary) para evitar setState em componente
  // desmontado. Combinado com o abort acima, da defesa em profundidade
  // contra promessas que resolvem apos o unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    // Só limpa o arquivo se não houver um arquivo selecionado aguardando envio
    if (fileName === "") {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setWrongDataType(false);
    }
  }, [inputDrawerInput]);
  // V3: a view "Geral" agora consome `inputsStatus` diretamente (via
  // OverviewV3), não depende mais de `generalStatus` carregado sob
  // demanda. Removido o useEffect legado que recarregava o snapshot
  // ao trocar para a view geral/responsável.
  useEffect(() => {
    i18n.changeLanguage(i18n.language.slice(0, 2));
    // Onda 1.1: snapshot inicial agora respeita unmount/abort. Antes,
    // o setState rodava mesmo com o componente desmontado (ex.: usuario
    // troca de view antes do snapshot chegar) e disparava warning do
    // React + leak. AbortController + mountedRef cobrem o caso.
    const initController = new AbortController();
    // Onda 2.0: endpoint UNICO ``/api/v3/inputs/state/`` (substitui
    // ``/snapshot/``). O subset ``data.runtime`` tem o mesmo shape
    // que o ``/snapshot/`` antigo devolvia, entao o
    // ``adaptSnapshotToV2Shape`` consome direto. Os outros campos
    // (``data.blocks``, ``data.drawer_details``) so sao usados em
    // ondas seguintes, quando os hooks ``useBlocksV3`` e
    // ``useDrawerDetailsV3`` migrarem para receber via prop.
    fetchInputsStateV3({ signal: initController.signal })
      .then(({ data: stateData }) => {
        if (!mountedRef.current || !stateData) return;
        const payload = stateData.runtime;
        const adapted = adaptSnapshotToV2Shape(payload);
        v3AppNameRef.current = adapted.implementation?.app_name || null;
        usersRef.current = adapted.users;
        const period = payload?.period;
        initialPeriodRef.current = period || null;
        setMaintenanceStatus(payload?.runtime?.maintenance || null);
        setMonthlyStatus(adapted.monthlyStatus);
        if (period && adapted.selectedMonthStatus) {
          setSelectedMonthCanonical({
            year: period.year,
            month: period.month,
            status: adapted.selectedMonthStatus,
            reason: adapted.selectedMonthStatusReason || null,
            active: adapted.selectedMonthStatusActive || null,
          });
        }
        setInputsStatus(adapted.inputsStatus);
        if (period) {
          lastRefreshParams.current = JSON.stringify({
            month: period.month,
            year: period.year,
          });
        }
      })
      .catch((error) => {
        if (!mountedRef.current) return;
        if (error?.name === "CanceledError") return;
        console.log(error);
      });
    return () => {
      initController.abort();
    };
  }, []);

  // endregion

  // region functions
  const getDate = (val, format_in, format_out = format_in) => {
    moment.locale(i18n.language);
    return moment(val, format_in).format(format_out).replace(".", "");
  };
  const checkIfAutoGeneratedInputsAreReady = (inputsStatus) => {
    for (const group of inputsStatus) {
      if (!Array.isArray(group.inputs)) continue;
      for (const input of group.inputs) {
        const dependencies = input.dependencies;
        const crossLinkingStage = input.stages?.cross_linking;
        if (
          dependencies !== null &&
          typeof dependencies === "object" &&
          Object.values(dependencies).every(
            (dep) =>
              Array.isArray(dep.data) &&
              dep.data.every((entry) => entry.ready === true),
          ) &&
          crossLinkingStage === "waiting"
        ) {
          return true;
        }
      }
    }
    return false;
  };
  const areAllRequiredInputsReady = (inputsStatus) => {
    let hasRequiredInputs = false;
    let allReady = true;
    for (const group of inputsStatus) {
      if (!Array.isArray(group.inputs)) continue;
      for (const input of group.inputs) {
        if (input.is_required) {
          hasRequiredInputs = true;
          const crossLinkingStage = input.stages?.cross_linking;
          if (
            crossLinkingStage !== "success" &&
            crossLinkingStage !== "warning"
          ) {
            allReady = false;
            break;
          }
        }
      }
      if (!allReady) break;
    }
    return hasRequiredInputs && allReady;
  };
  const isPipelineInProgress = (
    is = currentInputsStatus,
    md = selectedMonthData,
  ) => {
    if (md?.status === "running" || md?.status === "processing") return true;
    if (md?.pending_jobs_count > 0) return true;
    if (checkIfAutoGeneratedInputsAreReady(is)) return true;
    // If all required inputs are ready and status is "waiting", calculation should trigger soon
    if (md?.status === "waiting" && areAllRequiredInputsReady(is)) return true;
    return is.some((group) =>
      group.inputs?.some((input) => {
        const controlState = input.control_state;
        const phase = input.phase;
        // Onda 3.1: stages internos passaram a usar ``processing`` no
        // lugar de ``running``. Mantemos ambas as chaves como compat
        // defensivo (snapshot cacheado de ETag antigo etc.).
        return (
          controlState === "uploaded" ||
          controlState === "processing" ||
          phase === "queued" ||
          phase === "processing" ||
          input.stages?.data_source === "processing" ||
          input.stages?.data_source === "running" ||
          input.stages?.data_structure === "processing" ||
          input.stages?.data_structure === "running" ||
          input.stages?.cross_linking === "processing" ||
          input.stages?.cross_linking === "running"
        );
      }),
    );
  };
  // saveInputDetails foi removido desta view: o InputDrawerV3 passa a
  // gerenciar a edição das instruções do input via MarkdownEditor, que no
  // futuro fará a chamada a api/inputs_save_input_description/ diretamente
  // (ou ao endpoint V3 equivalente).

  const refreshInputsStatus = (silent = false, options = {}) => {
    if (!selectedMonthData) return false;
    const fade = !!options.fade;
    const params = {
      month: selectedMonthData.month,
      year: selectedMonthData.year,
    };
    const paramsKey = JSON.stringify(params);
    // Short-circuit: se ja temos runtime carregado pra esse
    // ``(year, month)`` (caso tipico: mount inicial ja hidratou
    // ``inputsStatus`` + ``lastRefreshParams``; o useEffect de
    // ``selectedMonth`` dispara este refresh logo depois pedindo o
    // mesmo mes), nao refazemos a request. ``silent=true`` (auto-poll
    // de pipeline em progresso) ignora o short-circuit porque
    // justamente quer o estado mais atualizado.
    if (
      !silent &&
      paramsKey === lastRefreshParams.current &&
      currentInputsStatus.length > 0
    ) {
      setIsRefreshFadeActive(false);
      setIsRefreshing(false);
      return false;
    }
    // PA-016 (2026-05-03): NAO limpamos mais ``inputsStatus`` aqui.
    // Antes, ``setInputsStatus([])`` no inicio do refresh derrubava
    // a lista para a tela do ``lowerSpinner``, e o usuario via:
    //   1. contadores zerarem (computeCounters sobre array vazio);
    //   2. lista sumir;
    //   3. spinner aparecer (com layout shift, pois o spinner usa
    //      ``minHeight: calc(100vh - 240px)``);
    //   4. lista voltar com os novos dados.
    //
    // A nova UX mantem os dados do mes anterior visiveis (com
    // opacidade reduzida via ``isRefreshing``) ate que os novos
    // cheguem. Sem flash, sem layout shift, sem contadores
    // momentaneamente em 0. O ``lowerSpinner`` continua aparecendo
    // SO no boot da view (quando ``inputsStatus.length === 0``
    // porque ainda nao houve nenhum fetch bem-sucedido).
    setIsRefreshFadeActive(fade && currentInputsStatus.length > 0);
    setIsRefreshing(true);
    lastRefreshParams.current = paramsKey;
    clearTimeout(autoRefreshTimeout.current);
    // Onda 1.1: o ``controller.current.signal`` agora vai para o axios
    // (antes era criado mas nunca passado — abort() era no-op para a
    // request, so funcionava para o guard ``if paramsKey !== ...``
    // logo abaixo). Com o signal passado, trocar de mes durante um
    // fetch realmente cancela a request HTTP.
    if (controller.current) controller.current.abort();
    controller.current = new AbortController();
    const reqController = controller.current;
    // Onda 2.0: endpoint UNICO ``/api/v3/inputs/state/`` (drop-in
    // substituindo ``fetchInputsV3Snapshot``). Sem ETag aqui
    // (refresh manual = "force refetch", o usuario acabou de fazer
    // uma acao OU o auto-poll ja sabe que ha coisa rodando).
    fetchInputsStateV3({ ...params, signal: reqController.signal })
      .then(({ data: stateData }) => {
        if (!mountedRef.current || !stateData) return;
        if (JSON.stringify(params) !== lastRefreshParams.current) return;
        const payload = stateData.runtime;
        const adapted = adaptSnapshotToV2Shape(payload);
        v3AppNameRef.current = adapted.implementation?.app_name || null;
        usersRef.current = adapted.users;
        setMaintenanceStatus(payload?.runtime?.maintenance || null);
        const monthData =
          adapted.monthlyStatus.find(
            (m) =>
              m.year === selectedMonthData.year &&
              m.month === selectedMonthData.month,
          ) || adapted.monthlyStatus[0];
        checkIfAutoGeneratedInputsAreReady(adapted.inputsStatus);
        setInputsStatus(adapted.inputsStatus);
        // Onda 2.1 (mai/2026): logica de "preserva optimismo via
        // ``uploadingInputsRef``" REMOVIDA. Substituida por
        // pause()/resume() no hook ``useInputsStateV3`` —
        // pollings em voo sao abortados quando uma acao comeca, e
        // o refresh pos-acao traz o estado real do backend. Sem
        // mais janelas de race entre otimismo e payload obsoleto.
        // PA-008: o snapshot devolve agregados de TODOS os meses da
        // janela operacional (nao so do mes selecionado). Antes
        // atualizavamos apenas o ``selectedMonthData`` no estado, o
        // que deixava os outros tabs do MonthSelectorV3 com agregados
        // antigos (ex.: usuario uploadava em Jan-24, isso disparava
        // xlinks que afetam Fev-24, mas o tracinho de Fev-24 so
        // refletia o novo estado quando o usuario clicava em Fev).
        // Substituimos a lista inteira pela versao do servidor —
        // ``adapted.monthlyStatus`` ja vem ordenado e completo.
        if (
          Array.isArray(adapted.monthlyStatus) &&
          adapted.monthlyStatus.length > 0
        ) {
          setMonthlyStatus(adapted.monthlyStatus);
        }
        const refreshedPeriod = payload?.period;
        if (refreshedPeriod && adapted.selectedMonthStatus) {
          setSelectedMonthCanonical({
            year: refreshedPeriod.year,
            month: refreshedPeriod.month,
            status: adapted.selectedMonthStatus,
            reason: adapted.selectedMonthStatusReason || null,
            active: adapted.selectedMonthStatusActive || null,
          });
        }
        if (monthData) setSelectedMonthData(monthData);
        // Onda 2.0 — passo D: auto-poll legado removido. O
        // ``useInputsStateV3`` cobre a cadencia (4s vivo / 30s
        // parado) com ETag, e o ``useEffect([inputsState.data])``
        // logo acima propaga o payload para inputsStatus/monthly
        // Status/canonical sem precisar de ``setTimeout`` aqui.
        // Esta funcao agora serve SO para refresh manual disparado
        // por handlers (upload/replicate/reprocess/etc.).
        setIsRefreshFadeActive(false);
        setIsRefreshing(false);
      })
      .catch((error) => {
        if (!mountedRef.current) return;
        if (error?.name === "CanceledError") return;
        console.error("Error refreshing inputs:", error);
        setIsRefreshFadeActive(false);
        setIsRefreshing(false);
      });
  };

  // Normaliza texto para busca "tolerante" — lowercase + remove acentos.
  // Usado tanto do lado da query (nameFilter) quanto do lado do nome de
  // cada input.
  const normalizeForSearch = (str) =>
    (str || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const getFilteredInputsStatus = () => {
    const hasResponsibleFilter = filteredResponsibleUsers.length > 0;
    const filteredIds = filteredResponsibleUsers.map((user) => user.id);
    const hasBadgeFilter = !!badgeFilter;
    const normalizedQuery = normalizeForSearch(nameFilter);
    const hasNameFilter = normalizedQuery.length > 0;
    if (!hasResponsibleFilter && !hasBadgeFilter && !hasNameFilter)
      return currentInputsStatus;
    const passesName = (input) => {
      if (!hasNameFilter) return true;
      const localized =
        (input.input_name && (input.input_name.pt || input.input_name.en)) ||
        input.meta_name ||
        "";
      const meta = input.meta_name || "";
      return (
        normalizeForSearch(localized).includes(normalizedQuery) ||
        normalizeForSearch(meta).includes(normalizedQuery)
      );
    };
    const passesResponsible = (input) => {
      if (!hasResponsibleFilter) return true;
      const respList = Array.isArray(input.responsible)
        ? input.responsible
        : [];
      if (respList.length === 0) {
        // Linha virtual "Sem responsável" é filtrável através do
        // id reservado NO_RESPONSIBLE_ID ("__no_responsible__").
        return filteredIds.includes("__no_responsible__");
      }
      return respList
        .map((resp) => resp.id)
        .some((id) => filteredIds.includes(id));
    };
    const passesBadge = (input) => {
      if (!hasBadgeFilter) return true;
      const { scope, type } = badgeFilter;
      if (scope === "mine" && !input.is_mine) return false;
      if (input.is_permanent_for_month) return false;
      // PA-019: ``delivered`` (tile "OK") deve corresponder estritamente
      // aos VERDES da barra/donut — bucket verde do winner-take-all:
      // ``delivered_ok`` (verde escuro) + ``in_progress`` (verde claro).
      // NAO usamos ``isInputDelivered``: essa funcao e' mais permissiva
      // (aceita warnings) e incluiria inputs amarelos no filtro.
      if (type === "delivered") {
        const st = classifyInputState(input);
        return st === "delivered_ok" || st === "in_progress";
      }
      // ``critical`` (PA-020) agora abrange tambem ``pending_decision``
      // — alinhado com o tile unico "Critico / aguarda decisao" e com
      // o bucket vermelho da barra de progresso. ``system_error``
      // (falha tecnica do pipeline) tambem entra aqui: cor propria
      // (preto) na UI mas mesmo agrupamento de "precisa intervir agora"
      // para fins de contador e filtro do badge.
      if (type === "critical") {
        const st = classifyInputState(input);
        return (
          st === "critical" ||
          st === "pending_decision" ||
          st === "system_error"
        );
      }
      // ``attention`` aglutina ``attention`` (warning consolidado) e
      // ``in_progress_attention`` (parcial com sinal amarelo) — mesmo
      // tile do expandido. Mantem a aglutinacao 9 -> 4 estados coerente
      // entre barra, donut e tile clicavel.
      if (type === "attention") {
        const st = classifyInputState(input);
        return st === "attention" || st === "in_progress_attention";
      }
      // ``not_sent`` aglutina ``not_sent`` (sem dado) e ``processing``
      // (parse em andamento) — ambos "ainda sem dado consolidado".
      if (type === "not_sent") {
        const st = classifyInputState(input);
        return st === "not_sent" || st === "processing";
      }
      return classifyInputState(input) === type;
    };
    let filteredInputsStatus = [];
    currentInputsStatus.map((group) => {
      let filteredGroup = {
        group_name: group.group_name,
        inputs: [],
      };
      group.inputs.map((input) => {
        if (
          passesResponsible(input) &&
          passesBadge(input) &&
          passesName(input)
        ) {
          filteredGroup.inputs.push(input);
        }
      });
      if (filteredGroup.inputs.length > 0)
        filteredInputsStatus.push(filteredGroup);
    });
    return filteredInputsStatus;
  };
  // endregion

  // region handlers
  const selectMonthIndex = (idx) => {
    if (
      !Array.isArray(monthlyStatus) ||
      idx < 0 ||
      idx >= monthlyStatus.length
    ) {
      return false;
    }
    const nextMonth = monthlyStatusV3[idx] || monthlyStatus[idx];
    if (!nextMonth) return false;
    setIsRefreshFadeActive(currentInputsStatus.length > 0);
    setIsRefreshing(true);
    setSelectedMonth(idx);
    setSelectedMonthData(nextMonth);
    return true;
  };

  const handleConnectionClick = (conn) => {
    setFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    setWrongDataType(false);
    setInputDrawerInput(conn);
  };
  // Click num "quadradinho" do OverviewV3 → abre o drawer do input.
  // Se o mês clicado difere do selecionado, alinhamos `selectedMonth`
  // primeiro (a tela toda passa a operar naquele mês). Quando o backend
  // V3 expuser histórico por input-mês, aqui entrará o warning de
  // descasamento no topo do drawer em vez do redirecionamento imediato.
  const handleOverviewOpenInput = (input, monthIdx) => {
    if (
      typeof monthIdx === "number" &&
      monthIdx !== selectedMonth &&
      monthlyStatus[monthIdx]
    ) {
      selectMonthIndex(monthIdx);
    }
    handleConnectionClick(input);
  };
  const handleProgressLogOpenInput = (ref) => {
    const metaName = ref?.input_name;
    if (!metaName) return;
    const monthIdx = monthlyStatusV3.findIndex(
      (m) => m.year === ref.year && m.month === ref.month,
    );
    if (monthIdx >= 0) {
      selectMonthIndex(monthIdx);
    }
    let target = null;
    for (const group of currentInputsStatus || []) {
      target = (group.inputs || []).find(
        (input) =>
          input.input_control_id === metaName || input.meta_name === metaName,
      );
      if (target) break;
    }
    if (target) {
      handleConnectionClick(target);
    }
  };
  const handleConfirmResetInput = () => {
    setInputsStatus([]);
    setIsRefreshing(true);
    let params = {
      month: selectedMonthData.month,
      year: selectedMonthData.year,
    };
    client
      .post("api/inputs_reset_month_inputs/", params)
      .then(() => {
        setResetInputsModalOpen(false);
        refreshInputsStatus();
      })
      .catch((error) => {
        console.log(error);
        setIsRefreshing(false);
      });
  };
  // Handlers de drag & drop + handleFileSelect foram removidos desta view.
  // O upload interativo (toolbar + drop zone) passa a viver dentro do
  // InputDrawerV3 e compartilhará a lógica de validação/envio quando a
  // pane de ações assumir o fluxo.

  // Atualiza o status do input otimisticamente (antes da resposta do
  // backend). Quando ``start === true`` (clique de upload/replicate/
  // reprocess), simulamos no frontend a MESMA semantica do cascade
  // ``invalidate_dependents`` que o backend aplica imediatamente:
  // todo veredicto do run anterior (parser + xlinks) deixa de valer.
  //
  // Bug regressivo (mai/2026): antes mexiamos so em ``stages.*`` mas
  // mantinhamos ``attention_label``/``control_state``/``cross_linking``
  // (raw) do run anterior. Resultado: input que estava CRITICAL antes
  // do reprocess continuava com pip vermelho ate o backend responder
  // (~500ms) e a timeline do drawer mostrava cruzamentos antigos
  // "instantaneamente apos o arquivo ser recebido". O ``classify
  // InputAggregate`` casa ``al === 'critical'`` ANTES do short-circuit
  // de ``data_source === 'processing'`` (Onda 3.1; antes ``'running'``),
  // entao otimismo parcial vazava o
  // estado vermelho. Solucao: zerar TUDO que represente veredicto
  // anterior — alinhado com o que o backend ja faz no clique.
  // ``updateInputStatusOptimistically`` REMOVIDO (onda 2.1 — mai/2026).
  // Otimismo de status do input gerava janela de inconsistencia entre
  // o reset local (T0) e a confirmacao do backend (T+200-500ms): um
  // fetch em voo do polling chegava no meio com estado pre-acao e
  // sobrescrevia o otimismo, fazendo a UI piscar para um veredicto
  // errado (ex.: "reenvio necessario" logo apos clicar reprocessar).
  // Substituimos por ``inputsState.pause()`` antes da acao +
  // ``inputsState.resume({refreshNow:true})`` ao final — payload
  // pos-acao chega coerente, sem janelas de race. Spinner
  // ``isUploading`` continua cobrindo o ciclo para feedback visual.

  // ``syncInputRuntimeFromDrawer`` REMOVIDO (onda 1.9 — mai/2026).
  // Era a propagacao drawer -> cockpit que regredia o cockpit quando o
  // polling do drawer (5s) ficava um tick atras do cockpit (4s).
  // Cockpit e' fonte unica para status do input agora; o
  // ``inputDrawerInput`` (header do drawer) ja e' sincronizado via o
  // useEffect que observa ``inputsStatus`` (linha ~557). Status do mes
  // e' mantido por ``onMonthStatusChange`` do ``useBlocksV3``, que e'
  // ortogonal a status de input.

  const {
    queueDrawerOpen,
    setQueueDrawerOpen,
    uploadQueueItems,
    setUploadQueueItems,
    setQueueRunning,
    setQueuePauseAfterCurrent,
    queueIgnoreUploaded,
    setQueueIgnoreUploaded,
    queueFolderInputRef,
    queueFileInputRef,
    queuePendingInputIdRef,
    queueNewSelectedInputId,
    setQueueNewSelectedInputId,
    queueSelectResetKey,
    setQueueSelectResetKey,
    canRunQueue,
    isQueueActive,
    addItemToQueue,
    removeItemFromQueue,
    moveItemUp,
    moveItemDown,
    clearQueue,
    handleQueuePickFile,
    handleQueueFolderSelected,
  } = useUploadQueue({
    userDetails,
    selectedMonthData,
    currentInputsStatus,
    inputsStatePause: inputsState.pause,
    inputsStateResume: inputsState.resume,
  });

  const handleMonthSelectionChange = (e, selectedMonth) => {
    if (isUploading || isQueueActive) {
      notify("Por favor aguarde até o término do upload", "warning");
      return false;
    }
    selectMonthIndex(selectedMonth);
    setUploadQueueItems([]);
    setQueueDrawerOpen(false);
    setQueuePauseAfterCurrent(false);
  };

  const specialOptionsApi = useSpecialOptionsModal({
    monthlyStatus,
    refreshInputsStatus,
    showUpdateDemoTab:
      userDetails?.implementation_name === INSIGHTS_DEMO_IMPLEMENTATION_NAME &&
      userDetails?.user_role === "DEV",
  });

  useEffect(() => {
    if (monthlyStatus.length > 0 && userDetails?.user_role === "DEV") {
      specialOptionsApi.checkActiveBatchJob();
      if (
        userDetails?.implementation_name === INSIGHTS_DEMO_IMPLEMENTATION_NAME
      ) {
        specialOptionsApi.checkActiveFillupJob();
      }
    }
  }, [
    monthlyStatus.length,
    userDetails?.implementation_name,
    userDetails?.user_role,
  ]);

  // Onda 1.2: hook unificado encapsula upload/replicate/reprocess.
  // Os 3 fluxos (handleUpload/Replicate/Reprocess) compartilham 80%
  // do codigo (setIsUploading + optimisticAdd + ref.add + API call +
  // cleanup + notify + refresh). O hook centraliza esse padrao,
  // expondo so a API limpa que os componentes consomem. Antes, cada
  // copia tinha pequenas variacoes que produziram leaks de
  // ``uploadingInputsRef`` quando o caminho de saida nao executava
  // o delete (ex.: ``api/awake/`` rejeitando antes do upload
  // comecar — agora ha ``.catch`` no nivel certo).
  const {
    isUploading,
    busyAction,
    uploadFile: uploadFileFromHook,
    replicate: replicateFromHook,
    reprocess: reprocessFromHook,
  } = useInputUpload({
    appNameRef: v3AppNameRef,
    selectedMonthData,
    inputDrawerInput,
    inputsStatePause: inputsState.pause,
    inputsStateResume: inputsState.resume,
    notify,
    userRole: userDetails?.user_role,
  });

  const handleReplicateFileClick = () => {
    const metaName = inputDrawerInput?.input_control_id;
    if (metaName) replicateFromHook(metaName);
  };
  const handleReprocessFileClick = () => {
    const metaName = inputDrawerInput?.input_control_id;
    if (metaName) return reprocessFromHook(metaName);
    return Promise.resolve(null);
  };
  const handleUploadFileClick = () => {
    const metaName = inputDrawerInput?.input_control_id;
    const file = fileInputRef.current?.files?.[0];
    if (metaName && file) {
      setUploadingInputName(getInputDisplayName(inputDrawerInput));
      uploadFileFromHook(metaName, file);
    }
  };

  // ---------------------------------------------------------------------
  // Drag & drop de arquivos (UI principal + drawer).
  // ---------------------------------------------------------------------
  // ``uploadingInputName`` guarda o nome LEGIVEL do input em envio para
  // alimentar o overlay de progresso na barra fixa superior. Limpamos
  // assim que o ciclo de upload termina (busyAction deixa de ser
  // "upload"). Replicate/reprocess nao alimentam esse overlay (sem
  // barra de progresso de arquivo).
  const [uploadingInputName, setUploadingInputName] = useState(null);
  useEffect(() => {
    if (busyAction !== "upload") setUploadingInputName(null);
  }, [busyAction]);

  // Nome legivel do input (mesma regra do ``InputBoxV3``): prioriza o
  // rotulo no idioma corrente, com fallbacks defensivos.
  const getInputDisplayName = useCallback(
    (inp) => {
      if (!inp) return "";
      const lang = i18n.language;
      return (
        (inp.input_name && inp.input_name[lang]) ||
        inp.input_name?.pt ||
        inp.display_name ||
        inp.meta_name ||
        inp.input_control_id ||
        ""
      );
    },
    [i18n.language],
  );

  // Notifica o motivo de um drop recusado (pasta / objeto que nao e
  // arquivo). Validacao de TIPO de arquivo fica a cargo do backend.
  const handleDropReject = useCallback(
    (reasonKey) => {
      const map = {
        folder: "inputs_v3.drop.reject_folder",
        not_file: "inputs_v3.drop.reject_not_file",
        empty: "inputs_v3.drop.reject_not_file",
      };
      notify(
        t(map[reasonKey] || "inputs_v3.drop.reject_not_file"),
        "warning",
        4000,
      );
    },
    [t],
  );

  // Handler unico de drop (card da UI principal e corpo do drawer).
  // Reaproveita exatamente o fluxo de upload do drawer (mesmo
  // ``uploadFileFromHook``), sem precisar abrir o drawer.
  const handleInputFileDrop = useCallback(
    (inp, file) => {
      const metaName = inp?.input_control_id;
      if (!metaName || !file) return;
      if (!canUploadInputs) {
        notify(t("inputs_v3.drop.no_permission"), "warning", 4000);
        return;
      }
      if (isUploading || isQueueActive) {
        notify(t("inputs_v3.drop.busy"), "info", 3000);
        return;
      }
      setUploadingInputName(getInputDisplayName(inp));
      uploadFileFromHook(metaName, file);
    },
    [
      canUploadInputs,
      isUploading,
      isQueueActive,
      uploadFileFromHook,
      getInputDisplayName,
      t,
    ],
  );

  // Guard global de navegacao do browser. Sem ele, soltar um arquivo
  // FORA de uma drop zone faz o navegador "abrir" o arquivo (saindo da
  // SPA e perdendo o estado). Prevenimos o ``default`` de dragover/drop
  // no ``window`` enquanto a view esta montada — apenas para drags que
  // carregam arquivos. As drop zones legitimas ja chamam
  // ``stopPropagation`` no seu proprio ``drop``, entao este handler so
  // atua nos drops "perdidos" (area neutra da pagina).
  useEffect(() => {
    const carriesFiles = (e) =>
      e?.dataTransfer &&
      Array.from(e.dataTransfer.types || []).includes("Files");
    const prevent = (e) => {
      if (carriesFiles(e)) e.preventDefault();
    };
    window.addEventListener("dragover", prevent);
    window.addEventListener("drop", prevent);
    return () => {
      window.removeEventListener("dragover", prevent);
      window.removeEventListener("drop", prevent);
    };
  }, []);
  // Download real do arquivo bruto entregue pelo cliente. ``kind`` vem
  // do ``DownloadMenu`` do drawer (``original`` / ``cleaned_up``); por
  // enquanto so suportamos ``original`` no backend V3 — a opcao
  // ``cleaned_up`` (canonical pos-parse) cai num toast de "ainda nao
  // disponivel" para nao quebrar a UI quando o usuario clicar.
  const [isDownloadingFile, setIsDownloadingFile] = useState(false);
  const handleDownloadInputDrawer = (kind, item) => {
    if (!inputDrawerInput) return;
    const metaName = inputDrawerInput.input_control_id;
    const appName = v3AppNameRef.current;
    if (!appName || !selectedMonthData || !metaName) return;
    // ``original`` = arquivo bruto; ``cleaned_up`` = versao processada
    // (parquet canonico -> xlsx, gerada on-the-fly pelo backend).
    const option = kind === "cleaned_up" ? "cleaned_up" : "original";
    // Period-aware: a HistoryPane passa o ``item`` da linha (que pode
    // ser de um mes ANTERIOR ao visualizado). Sem isso, baixar a linha
    // do mes passado serviria o periodo atual (bug latente que ja
    // afetava o ``original``). NowPane chama sem ``item`` -> mes atual.
    const periodYear =
      Number.isFinite(Number(item?.year)) && Number(item?.year) > 0
        ? Number(item.year)
        : selectedMonthData.year;
    const periodMonth =
      Number.isFinite(Number(item?.month)) && Number(item?.month) > 0
        ? Number(item.month)
        : selectedMonthData.month;
    if (isDownloadingFile) return;
    setIsDownloadingFile(true);
    // Onda 1.2: encadeamento ``client.post("api/awake/").then(downloadInputV3)``
    // unificado num unico promise chain. Antes, se ``api/awake/``
    // rejeitasse, o ``.finally`` interno nunca rodava e
    // ``setIsDownloadingFile(true)`` ficava preso (icone de spinner
    // eterno no botao de download).
    client
      .post("api/awake/")
      .then(() =>
        downloadInputV3({
          appName,
          year: periodYear,
          month: periodMonth,
          metaName,
          option,
        }),
      )
      .then(({ blob, filename }) => {
        fileDownload(blob, filename);
      })
      .catch((error) => {
        const errorCode = error?.response?.data?.error_code;
        if (errorCode) {
          notify(t("modeling_inputs_v2.errors." + errorCode), "error", 5000);
        } else {
          console.log(error);
        }
      })
      .finally(() => {
        setIsDownloadingFile(false);
      });
  };
  // Handlers legados do drawer V2 (retry, download com popper de opções)
  // foram removidos. O InputDrawerV3 simplifica para "Baixar última
  // versão" e delegará o retry/reprocess à pane "Configuração" quando
  // essa opção for reintroduzida.

  // Salvar responsaveis do input ativo no drawer V3.
  //
  // Endpoint V3 dedicado: ``api/v3/inputs/<meta_name>/responsibles/``.
  // Antes reusavamos o V2 ``inputs_save_responsible`` mas ele esperava
  // ``Input.id`` numerico — em V3 o identificador e ``meta_name``,
  // levando ao bug ``ValueError: Field 'id' expected a number but
  // got 'descuentos_centralizacion_smk'`` reportado pelo consultor.
  //
  // O backend V3:
  //   - Aceita lista de User PKs numericos (canonico em V3).
  //   - Valida papel (DEV/IMG/CAD) e que cada user pertence a
  //     implementacao ativa.
  //   - Substitui a lista inteira em transaction.
  //
  // Optimistic update: atualizamos ``inputDrawerInput.responsible``
  // e a entrada equivalente em ``inputsStatus`` antes da resposta;
  // se falhar, mostramos toast e o pane mantem o draft.
  const handleSaveResponsibles = (newResponsibleList) => {
    if (!inputDrawerInput) return;
    if (isSavingResponsibles) return;
    const metaName = inputDrawerInput.meta_name || inputDrawerInput.input_id;
    const params = {
      user_ids: (newResponsibleList || [])
        .map((r) => Number(r.id))
        .filter((n) => Number.isFinite(n)),
    };
    setIsSavingResponsibles(true);
    // Padrao canonico de acao da view (mesmo de upload/replicate):
    // ``pause()`` aborta o poll em voo (evita que um payload pre-save
    // chegue depois e sobrescreva), e ``resume({refreshNow:true})`` no
    // fim forca um refetch IMEDIATO ignorando o ETag local. Sem isso,
    // a UI principal (``currentInputsStatus`` = payload do hook) so
    // refletiria o novo responsavel no proximo tick do poll — ate ~30s
    // em mes terminal. O update otimista abaixo cobre apenas o caso de
    // fallback (``liveSnapshot`` ainda nulo no boot).
    inputsState.pause();
    client
      .post(
        `api/v3/inputs/${encodeURIComponent(metaName)}/responsibles/`,
        params,
      )
      .then((response) => {
        const saved = response?.data?.responsibles || newResponsibleList;
        setInputDrawerInput((draft) => {
          if (draft) draft.responsible = saved;
        });
        setInputsStatus((draft) => {
          for (let i = 0; i < draft.length; i += 1) {
            const inputs = draft[i].inputs || [];
            for (let j = 0; j < inputs.length; j += 1) {
              if (inputs[j].meta_name === metaName) {
                inputs[j].responsible = saved;
                return;
              }
            }
          }
        });
        notify(t("modeling_inputs_v2.responsible_saved"), "success", 2000);
      })
      .catch((error) => {
        const errorCode = error?.response?.data?.error_code;
        notify(
          t(errorCode ? "groups.errors." + errorCode : "server_error"),
          "danger",
          3000,
        );
      })
      .finally(() => {
        setIsSavingResponsibles(false);
        // Retoma o polling com refetch imediato (ignora ETag): traz o
        // payload fresco ja com a lista salva. Combinado com o fix do
        // ETag (``InputResponsibleV3`` no token), os ticks seguintes
        // tambem permanecem consistentes.
        inputsState.resume({ refreshNow: true });
      });
  };

  // "Marcar como analisado" de um warning (sev=2) da timeline do input.
  // Mesmo padrao canonico de acao da view: ``pause()`` antes do POST +
  // ``resume({refreshNow:true})`` ao final. Como o drawer e a grid
  // compartilham ``inputsState`` (``drawerDetails`` e' override do
  // payload consolidado), um unico refetch atualiza de uma vez: o
  // carimbo na timeline, o pip, o status do mes, os contadores e o
  // "status por responsavel". Retorna a Promise para o NowPane controlar
  // o spinner local da caixa enquanto a chamada esta em voo.
  const handleMarkNoteVerifiedV3 = (noteId, verified = true) => {
    if (!noteId) return Promise.resolve();
    inputsState.pause();
    return markInputNoteVerifiedV3({ noteId, verified })
      .catch((error) => {
        notify(
          t("modeling_inputs_v3.now_pane.timeline.mark_error"),
          "danger",
          3000,
        );
        throw error;
      })
      .finally(() => {
        inputsState.resume({ refreshNow: true });
      });
  };

  const handleRefreshInputsClick = () => {
    if (isRefreshing) return false;
    refreshInputsStatus(true);
  };
  // PA-021 (mai/2026): o botao "Reset mes" inline foi removido junto
  // com a migracao da ``monthlyStatus`` antiga para a
  // ``InputsV3GlobalTopBar``. O reset continua disponivel via aba
  // "ADM > Ações" do ``ProgressDrawerV3`` (``MonthAdminActionsPane``),
  // que dispara o mesmo endpoint atraves de
  // ``setResetInputsModalOpen`` no ``ResetInputsModal``.
  const handleMarkAsAnalyzedClick = (uuid) => {
    setInputControlsMarkedAsVerifiedLine((draft) => {
      draft.push(uuid);
    });
    const params = { uuid };
    client
      .post("api/inputs_note_mark_as_verified/", params)
      .then((response) => {
        setInputsStatus((draft) => {
          draft.forEach((item) => {
            item.inputs.forEach((input) => {
              input.notes.forEach((note) => {
                if (note.uuid === uuid) {
                  note.marked_as_verified = true;
                  note.marked_as_verified_by = response.data.username;
                  if (response.data.change_to_success_status) {
                    if (response.data.check_type === "STR") {
                      input.stages.data_structure = "success";
                    } else if (response.data.check_type === "CSL") {
                      input.stages.cross_linking = "success";
                    }
                  }
                }
              });
            });
          });
        });
        setInputDrawerInput((draft) => {
          const note = draft.notes.find((note) => note.uuid === uuid);
          if (note) {
            note.marked_as_verified = true;
            note.marked_as_verified_by = response.data.username;
          }
          if (response.data.change_to_success_status) {
            if (response.data.check_type === "STR") {
              draft.stages.data_structure = "success";
            } else if (response.data.check_type === "CSL") {
              draft.stages.cross_linking = "success";
            }
          }
        });
        setInputControlsMarkedAsVerifiedLine((draft) => {
          const index = draft.indexOf(uuid);
          if (index !== -1) draft.splice(index, 1);
        });
      })
      .catch((error) => {
        setInputControlsMarkedAsVerifiedLine((draft) => {
          const index = draft.indexOf(uuid);
          if (index !== -1) draft.splice(index, 1);
        });
        console.log(error);
      });
  };
  // Edição de responsável foi removida desta view — será reintegrada na
  // pane "Configuração" do InputDrawerV3 quando o endpoint V3 estiver
  // disponível. O filtro por responsável (abaixo) continua operando.

  // endregion

  // region component fragments
  // V3: barra de seleção de meses substituída pelo MonthSelectorV3
  // (variação F2 segmented-control, validada no sandbox HTML). O novo
  // componente consome a shape V2 direto e mapeia o status para o
  // vocabulário V3 (waiting/action/done/stale) internamente. Os
  // imports `CustomTabs`/`CustomTab` foram removidos.
  const monthSelectionTabs = () => {
    return (
      <MonthSelectorV3
        monthlyStatus={monthlyStatusV3}
        selectedMonth={selectedMonth}
        onSelectMonth={(idx) => handleMonthSelectionChange(null, idx)}
        pendingDecisionsByMonth={pendingDecisionsByMonth}
      />
    );
  };
  // ``getMonthProgressIcon`` foi migrado para dentro do
  // ``ModelingProgressBandV3`` junto com o chip de status do mes (PA-019).
  // Os imports dos icones (Alarm, Cached, Help, etc.) seguem usados em
  // outros pontos da view, por isso permanecem.
  // Icone central representa a "natureza" da informacao:
  // - arquivo: upload tradicional pelo usuario (InsertDriveFile)
  // - semifixo: editado diretamente na interface, podendo receber
  //   ajustes automaticos do sistema (Tune, sliders)
  // - conexao: integracao direta com sistema do cliente via API/sync
  //   (Sync, setas circulares) - preparado para V4, ainda nao usado
  // A "caixa de input" oficial do V3 é um componente dedicado
  // (inputs_v3_components/InputBoxV3). O wrapper abaixo só conecta o
  // onClick à lógica legada de navegação/drawer.
  // ``InputBoxV3`` agora e renderizado pelos ``InputGroupCardV3`` (o
  // agrupamento colapsavel). Mantemos este helper para consumidores
  // legados (nao ha mais chamadas diretas a ele na view, mas vale
  // preservar pra debug/storybook do monolito).
  const inputBox = (input, key, displayResponsible) => (
    <InputBoxV3
      key={key}
      input={input}
      onClick={(inp) => handleConnectionClick(inp)}
      displayResponsible={displayResponsible}
    />
  );
  void inputBox;

  // V3 only: ordenação alternativa. Achatamos os grupos filtrados em uma
  // lista única, e:
  //   - "attention":   ordena por severidade executiva (crítico → entregue ok).
  //   - "responsible": agrupa virtualmente por responsável (primário), e
  //                    dentro de cada pessoa ordena por severidade. Inputs
  //                    sem responsável caem em uma seção final.
  //
  // Ranking de atenção (menor = mais atenção): **não-enviado fica acima de
  // entregue-ok** porque "o que falta" tem grau de atenção maior que "o que
  // já está concluído e verde". Ordem canônica:
  //   critical → attention → warning → not_sent → delivered_ok.
  const ATTENTION_RANK = {
    // ``system_error`` compartilha o topo com ``critical``: ambos
    // bloqueiam o avanco e exigem intervencao imediata. Pintura
    // diferente (preto vs vermelho), prioridade igual.
    system_error: 0,
    critical: 0,
    attention: 1,
    warning: 2,
    processing: 3,
    in_progress: 4,
    not_sent: 5,
    delivered_ok: 6,
  };
  const attentionRank = (input) => {
    const state = classifyInputState(input);
    return ATTENTION_RANK[state] != null ? ATTENTION_RANK[state] : 99;
  };
  const flatFilteredInputs = () => {
    const groups = getFilteredInputsStatus();
    const flat = [];
    for (const g of groups) {
      for (const inp of g.inputs || []) flat.push(inp);
    }
    return flat;
  };
  // ``flatBlock`` foi removido — todos os modos agora produzem uma
  // lista de "grupos" com o mesmo shape e sao renderizados pelo
  // ``InputGroupListV3``, que controla o accordion. Ver
  // ``buildGroupsForMode`` abaixo.

  // V3 only: toggle de ordenação, renderizado acima da lista.
  // Visual unificado com o toggle "Vista detalhada / Vista geral" do card
  // principal: mesmo `Button color="client"`, mesma combinação
  // firstButton/middleButton/lastButton + buttonSelected, mesmos paddings.
  // Embrulhado em uma Card curta com background hachurado diagonal
  // (branco × cinza claro) para dar a sensação de "barra de ferramentas
  // sobre superfície" sem inflar hierarquia visual.
  const sortToggleBar = () => {
    const options = [
      {
        key: "group",
        label: t("inputs_v3.controls.sort_by_group", "Por grupo"),
        position: "first",
      },
      {
        key: "attention",
        label: t("inputs_v3.controls.sort_by_attention", "Por atenção"),
        position: "middle",
      },
      {
        key: "responsible",
        label: t("inputs_v3.controls.sort_by_responsible", "Por responsável"),
        position: "last",
      },
    ];
    return (
      <Card
        style={{
          minWidth: "1200px",
          marginTop: "8px",
          marginBottom: "0px",
          background: "#ffffff",
        }}
      >
        <CardBody style={{ padding: "2px 10px 4px" }}>
          <div
            style={{
              // Barra em duas pontas: busca na esquerda, seletor de
              // ordenacao na direita. Antes o seletor vinha centralizado
              // sozinho; agora a esquerda carrega o ``TextField`` de
              // busca por nome e o seletor encosta na direita.
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              padding: "4px 0 0 0",
            }}
          >
            <div style={{ flex: "0 1 320px", minWidth: "180px" }}>
              {/* Honeypot anti-autofill: campo "fake" antes do real
                  para o navegador escolher esse para autofill em vez
                  do campo de busca. Mantemos invisível com tabIndex=-1
                  e aria-hidden para não atrapalhar acessibilidade. */}
              <input
                type="text"
                name="username"
                autoComplete="username"
                tabIndex={-1}
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: -10000,
                  top: -10000,
                  width: 1,
                  height: 1,
                  opacity: 0,
                }}
                readOnly
              />
              <TextField
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder={t(
                  "inputs_v3.controls.search_placeholder",
                  "Buscar input por nome",
                )}
                variant="standard"
                size="small"
                fullWidth
                autoComplete="off"
                name={`inputs-v3-filter-${Math.random().toString(36).slice(2, 8)}`}
                onFocus={(e) => e.target.removeAttribute("readonly")}
                inputProps={{
                  // Camada extra contra autofill: o campo entra
                  // ``readOnly`` no mount, e libera no primeiro focus.
                  // Chrome ignora o autofill em campos readOnly e nao
                  // tenta de novo apos a edição inicial. Combinado com
                  // o honeypot acima e os atributos tradicionais
                  // (autocomplete, data-lpignore, name aleatório) cobre
                  // 1Password, LastPass, Chrome/Edge nativos.
                  readOnly: true,
                  autoComplete: "new-password",
                  "data-lpignore": "true",
                  "data-form-type": "other",
                  "data-1p-ignore": "true",
                  role: "presentation",
                  spellCheck: "false",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  style: {
                    padding: 0,
                    height: 24,
                    lineHeight: "24px",
                  },
                }}
                InputProps={{
                  disableUnderline: true,
                  style: {
                    background: "#f1f3f4",
                    borderRadius: 6,
                    display: "flex",
                    alignItems: "center",
                    padding: "4px 10px",
                    minHeight: 32,
                    fontSize: "0.85rem",
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search
                        fontSize="small"
                        style={{ color: grayColor[1] }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: nameFilter ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label={t(
                          "inputs_v3.controls.clear_search",
                          "Limpar busca",
                        )}
                        onClick={() => setNameFilter("")}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </div>
            <div
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                verticalAlign: "middle",
              }}
            >
              <span
                style={{
                  fontSize: "0.72rem",
                  color: grayColor[6],
                  whiteSpace: "nowrap",
                  textTransform: "none",
                  letterSpacing: "0.02em",
                }}
              >
                {t("inputs_v3.controls.sort_prefix", "Organizar por")}
              </span>
              <div style={{ position: "relative", display: "inline-block" }}>
                {options.map((opt) => {
                  const active = sortMode === opt.key;
                  return (
                    <Button
                      key={opt.key}
                      size={"sm"}
                      color={"client"}
                      simple={!active}
                      className={cx({
                        [classes.viewButton]: true,
                        [classes.firstButton]: opt.position === "first",
                        [classes.middleButton]: opt.position === "middle",
                        [classes.lastButton]: opt.position === "last",
                        [classes.buttonSelected]: active,
                      })}
                      onClick={() => setSortMode(opt.key)}
                    >
                      {opt.label}
                    </Button>
                  );
                })}
              </div>
              {/* Toggle "abrir/fechar todos" — afforance textual discreta
                  solicitada pelo usuario (2026-04-24). Mesma fonte do
                  label "Organizar por", cinza com sublinhado para
                  comunicar "link". Rotulo em minusculo por decisao
                  estilistica do usuario. Calculado lazy (o computo
                  de grupos depende de ``classifyInputState`` declarado
                  mais abaixo — TDZ se fosse eager). */}
              {(() => {
                const groupsForToggle = getCurrentGroups();
                if (groupsForToggle.length === 0) return null;
                const allOpen = isAllOpen(groupsForToggle);
                return (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleAllGroups(groupsForToggle)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleAllGroups(groupsForToggle);
                      }
                    }}
                    style={{
                      fontSize: "0.72rem",
                      color: grayColor[6],
                      marginLeft: 10,
                      cursor: "pointer",
                      textDecoration: "underline",
                      whiteSpace: "nowrap",
                      userSelect: "none",
                    }}
                  >
                    {allOpen
                      ? t("inputs_v3.controls.collapse_all", "fechar todos")
                      : t("inputs_v3.controls.expand_all", "abrir todos")}
                  </span>
                );
              })()}
              {/* Rev. 2026-05-26: pill da legenda MIGRADO daqui para
                  o lado direito da barra de avanco (entre a barra
                  colorida e o chevron de expand). Mantemos os outros
                  controles desta barra (sort + abrir/fechar todos)
                  intactos. */}
            </div>
          </div>
        </CardBody>
      </Card>
    );
  };

  // Definicao dos blocos do modo "Por atencao". Cada bloco mapeia 1+
  // estados canonicos (``classifyInputAggregate``) — dentro do bloco,
  // a ordem dos sub-estados respeita ``subStates`` (1o primeiro).
  //
  // Reagrupamento (2026-06-01, diretiva consultor): 4 grupos, "mais
  // agregados", nesta ordem de cima para baixo, com a sub-ordem
  // pedida dentro de cada um:
  //   1. Nao enviados   | aguardando processamento (parsing) > nao
  //                       enviado de todo.
  //   2. Criticos       | pergunta aberta > rejeitado > erro de
  //                       processamento.
  //   3. Em atencao     | COM pendencia de cruzamento > SEM.
  //   4. OK             | COM pendencia de cruzamento > SEM.
  //
  // Mapeamento "pendencia de cruzamento": os estados ``in_progress*``
  // ("aguardando para avancar") sao exatamente os inputs entregues
  // cujos cruzamentos ainda estao em curso/aguardando contraparte —
  // por isso vem ANTES do estado "assentado" (``attention`` puro em
  // 3, ``delivered_ok`` em 4). ``processing`` (parse em curso) entra
  // no grupo "Nao enviados" porque o input ainda nao esta utilizavel.
  //
  // ``titleKey`` aponta para chaves em
  // ``inputs_v3.sort_attention.block_*`` (pt/en/es) para que os
  // headers do accordion respeitem o idioma ativo.
  const ATTENTION_GROUPS = [
    {
      id: "att-not-sent",
      titleKey: "inputs_v3.sort_attention.block_not_sent",
      subStates: ["processing", "not_sent"],
    },
    {
      id: "att-critical",
      titleKey: "inputs_v3.sort_attention.block_critical",
      // Agrupa os tres estados que TRAVAM o avanco do mes: pergunta
      // aberta (D-22), rejeicao por validacao (``critical``) e falha
      // tecnica do pipeline (``system_error``). Cada um mantem sua cor
      // propria no card (hachurado / vermelho / preto); aqui so
      // compartilham o bloco e a sub-ordem.
      subStates: ["pending_decision", "critical", "system_error"],
    },
    {
      id: "att-attention",
      titleKey: "inputs_v3.sort_attention.block_attention",
      subStates: ["in_progress_attention", "attention"],
    },
    {
      id: "att-ok",
      titleKey: "inputs_v3.sort_attention.block_ok",
      subStates: ["in_progress", "delivered_ok"],
    },
  ];

  // Builda a lista de grupos para o ``InputGroupListV3`` a partir
  // do modo de ordenacao atual. Cada grupo segue a shape
  // ``{ id, title, inputs, displayResponsibleName? }``.
  const buildGroupsForMode = () => {
    if (sortMode === "attention") {
      // Bucketiza por ``classifyInputAggregate`` e despeja em cada
      // bloco na ordem dos ``subStates``. Blocos vazios sao
      // descartados pra nao poluir a lista com cabecalho "Em
      // atencao: 0".
      const flat = flatFilteredInputs();
      const byState = new Map();
      for (const inp of flat) {
        const st = classifyInputState(inp);
        if (!byState.has(st)) byState.set(st, []);
        byState.get(st).push(inp);
      }
      const groups = [];
      for (const g of ATTENTION_GROUPS) {
        const merged = [];
        for (const st of g.subStates) {
          const bucket = byState.get(st);
          if (bucket && bucket.length) merged.push(...bucket);
        }
        if (merged.length === 0) continue;
        groups.push({
          id: g.id,
          title: t(g.titleKey),
          inputs: merged,
        });
      }
      return groups;
    }
    if (sortMode === "responsible") {
      const flat = flatFilteredInputs();
      // Mapa: respKey → { name, inputs[] }. respKey é id quando disponível;
      // caso contrário, cai no name. Sem responsável vira key reservada.
      const NO_RESP = "__no_responsible__";
      const map = new Map();
      for (const inp of flat) {
        const list = Array.isArray(inp.responsible_users)
          ? inp.responsible_users
          : Array.isArray(inp.responsible)
            ? inp.responsible
            : [];
        if (list.length === 0) {
          const bucket = map.get(NO_RESP) || {
            key: NO_RESP,
            name: "Sem responsável",
            isMissing: true,
            isCurrent: false,
            inputs: [],
          };
          bucket.inputs.push(inp);
          map.set(NO_RESP, bucket);
          continue;
        }
        // Input com múltiplos responsáveis entra em cada bloco (by design
        // — segue o mesmo critério do card "Status por responsável").
        for (const r of list) {
          const key = r.id || r.email || r.name;
          const bucket = map.get(key) || {
            key,
            name: r.name || "—",
            isMissing: false,
            isCurrent: !!r.is_current_user,
            inputs: [],
          };
          bucket.inputs.push(inp);
          map.set(key, bucket);
        }
      }
      // Quando há filtro de responsável ativo, só renderizamos blocos dos
      // responsáveis filtrados. O filtro em `getFilteredInputsStatus()` já
      // garante que só chegam inputs onde pelo menos um dos filtrados é
      // dono — aqui descartamos os blocos "colaterais" (donos extras dos
      // mesmos inputs que não foram explicitamente pedidos). Resultado:
      // input {Ana, Maria} com filtro "Maria" aparece só no bloco Maria.
      const filterIds = filteredResponsibleUsers.map((u) => u.id);
      const hasRespFilter = filterIds.length > 0;
      // Ordenação dos blocos: usuário atual primeiro, depois alfabético.
      // "Sem responsável" sempre ao final.
      let buckets = Array.from(map.values()).sort((a, b) => {
        if (a.isMissing !== b.isMissing) return a.isMissing ? 1 : -1;
        if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
      if (hasRespFilter) {
        buckets = buckets.filter((b) => filterIds.includes(b.key));
      }
      return buckets.map((b, idx) => {
        const sorted = b.inputs
          .slice()
          .sort((x, y) => attentionRank(x) - attentionRank(y));
        const overrideName = b.isMissing ? undefined : b.name;
        return {
          id: `flat-resp-${b.key || idx}`,
          title: b.name,
          inputs: sorted,
          displayResponsibleName: overrideName,
        };
      });
    }
    return getFilteredInputsStatus().map((input_group, key1) => ({
      id: `group-${input_group.group_name?.pt || key1}`,
      title: input_group.group_name[i18n.language],
      inputs: input_group.inputs,
    }));
  };

  // ``buildGroupsForMode`` depende (via ``attentionRank``) de
  // ``classifyInputState``, que e declarado mais abaixo no corpo
  // do componente. Chamar ``buildGroupsForMode()`` eagerly aqui
  // causava TDZ (``Cannot access 'classifyInputState' before
  // initialization``) assim que o usuario trocasse para "Por
  // atencao" ou "Por responsavel". A solucao e manter o calculo
  // LAZY — o resultado so e necessario durante a renderizacao
  // (``sortToggleBar`` / ``inputsComponent``), que acontece no
  // return do componente, quando todas as declaracoes ja estao
  // inicializadas.
  const getCurrentGroups = () => buildGroupsForMode();
  const isAllOpen = (groups) =>
    groups.length > 0 && groups.every((g) => openGroupIds.has(g.id));
  const toggleAllGroups = (groups) => {
    if (isAllOpen(groups)) {
      setOpenGroupIds(new Set());
    } else {
      setOpenGroupIds(new Set(groups.map((g) => g.id)));
    }
  };

  const inputsComponent = () => {
    const groups = getCurrentGroups();
    if (groups.length === 0) return null;
    return (
      <InputGroupListV3
        groups={groups}
        openIds={openGroupIds}
        onToggle={toggleGroup}
        onInputClick={(inp) => handleConnectionClick(inp)}
        clientColor={clientColor}
        onInputFileDrop={canUploadInputs ? handleInputFileDrop : undefined}
        onDropReject={handleDropReject}
      />
    );
  };
  const monthlyStatusDiv = () => {
    // PA-021 (mai/2026): a "monthlyStatus" antiga (mes + refresh +
    // fila + reset inline) foi consolidada na nova
    // ``InputsV3GlobalTopBar`` (Variante D do sandbox). Esta funcao
    // segue como ponto de plug-in unico — quem chama nao precisa
    // saber se a barra mudou. O posicionamento ``position: fixed;
    // top: 0`` vem de ``classes.monthlyStatusV3`` (variante "sem
    // chassi" de ``classes.monthlyStatus``: a V3 nao precisa do
    // fundo/borda da classe-mae porque o proprio
    // ``InputsV3GlobalTopBar`` ja desenha o chassi internamente —
    // 800x44px, border-radius nas pontas inferiores).
    //
    // Os tres tiles ("Entregues" / "Pendências" / "Cálculos") abrem
    // o ``ProgressDrawerV3`` na aba correspondente (ver
    // ``openProgressDrawerOnTab`` mais acima). O refresh continua
    // sendo o unico botao de acao global. "Reset mes" / "Fila dev"
    // vivem dentro do drawer (aba "ADM > Ações") e do
    // ``UploadQueueDrawer``, respectivamente — ambos acessiveis via
    // outros caminhos.

    // Frase do estado precisa do status canonico — usa
    // ``selectedMonthStatusV3`` (Onda 1.8c) ja calculado mais acima.
    const monthLabelNode = (
      <span>
        {t("months." + selectedMonthData.month.toString())}{" "}
        <em>· {selectedMonthData.year.toString()}</em>
      </span>
    );

    // Contadores ------------------------------------------------------
    // Topbar usa o ``actions_count_unique`` (1 contagem por pendencia,
    // sem expansao por responsavel). O ``actions_count`` "cru" continua
    // sendo consumido pelo ``CockpitStrip`` + matriz do ``ActionsPane``
    // — la a expansao por pessoa e' a semantica desejada.
    const counters = computeCounters();
    const acu =
      progressOverview.data?.actions_count_unique ||
      progressOverview.data?.actions_count;
    const pendingCount = acu
      ? (acu.decisions || 0) +
        (acu.uploads || 0) +
        (acu.reenvios || 0) +
        (acu.errors || 0)
      : 0;
    const blocksList = inputsState.data?.blocks?.blocks || [];
    const blocksTotal = blocksList.length;
    const blocksDone = blocksList.filter((b) => b?.status === "done").length;

    return (
      <div className={classes.monthlyStatusV3}>
        <InputsV3GlobalTopBar
          monthLabel={monthLabelNode}
          selectedMonthStatusV3={selectedMonthStatusV3}
          selectedMonthStatusReasonV3={selectedMonthStatusReasonV3}
          selectedMonthStatusActiveV3={selectedMonthStatusActiveV3}
          delivered={counters.delivered}
          total={counters.total}
          pendingCount={pendingCount}
          blocksDone={blocksDone}
          blocksTotal={blocksTotal}
          isRefreshing={isRefreshing}
          refreshDisabled={isRefreshing || currentInputsStatus.length === 0}
          onRefresh={() => handleRefreshInputsClick()}
          onOpenPendings={() => openProgressDrawerOnTab("actions")}
          onOpenBlocks={() => openProgressDrawerOnTab("blocks")}
          onOpenTimeline={() => openProgressDrawerOnTab("timeline")}
          transitionFaded={isRefreshFadeActive}
          uploadingName={
            busyAction === "upload" && !inputDrawerInput
              ? uploadingInputName
              : null
          }
          uploadProgress={uploadProgressValue}
        />
      </div>
    );
  };

  // Spinner exibido na transicao entre meses (apos clicar em um mes
  // diferente, enquanto ``inputsStatus`` esta sendo recarregado) e
  // no log de calculo. Usamos o ``Grid`` do ``react-loader-spinner``
  // em vez do ``ThreeDots`` historico para diferenciar "carregamento
  // de uma area inteira" de "operacao pontual em andamento" (que
  // segue como ThreeDots em botoes/linhas).
  //
  // Sem texto auxiliar: o contexto (a propria area que se esta
  // recarregando) ja comunica o que esta carregando, e o spinner
  // sozinho deixa a transicao mais limpa visualmente. O boot da
  // view e tratado pelo ``FullPageLoader``, que tem texto/logo.
  //
  // ``minHeight: calc(100vh - 240px)`` faz o spinner se posicionar
  // proximo ao centro vertical da viewport (descontando os ~240px
  // ocupados pelo header da aplicacao + breadcrumb + margens).
  const lowerSpinner = (
    <div
      style={{
        width: "100%",
        minHeight: "calc(100vh - 240px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <GridSpinner
        color={clientColor}
        height="44"
        width="44"
        ariaLabel="loading"
        visible={true}
      />
    </div>
  );

  // region top counters (V3)
  //
  // Dois cards 50/50 no topo: "Todos os inputs" e "Seus inputs".
  // - Denominador exclui inputs permanentes (singleton / yearly /
  //   monthly+reuse_auto) — eles nao fazem parte do ciclo mensal
  //   (D-21). (Antes da rev. 2026-05-28 / migration 0035 este grupo
  //   era ``semi_fixed`` + ``fixed``; ``semi_fixed`` foi consolidado
  //   em ``monthly`` + flag ``reuse_auto``.)
  // - Entregue = data_source === "success" (cliente ja mandou algo).
  // - Critico = algum stage `error`/`system_error`.
  // - Em atencao = algum stage `warning` (e nao esta em critico).
  //
  // Altura igual via GridItem display:flex + Card flex:1.
  // Clicar no badge faz fade nos inputs fora do escopo/estado filtrado.
  // PA-006: classificacao agora vem da fonte canonica
  // (services/inputsV3StateRules.js). Mantemos o mesmo nome
  // ``classifyInputState`` para nao quebrar referencias internas
  // (computeCounters, attentionRank, passesBadge, etc.).
  const classifyInputState = classifyInputAggregate;
  // PA-010 (rev. 2026-04-30) — definicao de "entregue" para o
  // contador do topo. A regra de fato vive em
  // ``services/inputsV3StateRules.js → isInputDelivered``. Resumo:
  //
  //   Entregue ⇔ parsing concluiu sem erros (data_structure success
  //               ou warning) E cross-linking nao esta em estado
  //               bloqueante (error / system_error / pergunta).
  //
  // Casos:
  //   - acabou de subir / parseando ......... NAO entregue
  //   - parsing OK, xlinks nao rodaram ...... entregue
  //   - parsing OK + xlink em atencao ....... entregue
  //   - parsing OK + xlink critico .......... NAO entregue
  //
  // ``deliveredClean`` mantem o sentido de "entregue E sem nenhum
  // sinal amarelo" — usado no tooltip "X entregues (Y limpos)".
  const computeCounters = (predicate) => {
    let total = 0;
    let delivered = 0;
    let deliveredClean = 0;
    let inProgress = 0;
    let pendingDecision = 0;
    let critical = 0;
    let attention = 0;
    let notSent = 0;
    const states = [];
    for (const group of currentInputsStatus) {
      for (const inp of group.inputs || []) {
        if (predicate && !predicate(inp)) continue;
        total += 1;
        const state = classifyInputState(inp);
        states.push(state);
        // Numerador do donut "Entregue" — fonte canonica.
        const isDelivered = isInputDelivered(inp);
        if (isDelivered) {
          delivered += 1;
          if (!hasAttentionSignal(inp)) deliveredClean += 1;
        }
        // Buckets exclusivos (winner-take-all) para os chips abaixo
        // do donut. Nao confundir com ``isDelivered``: um xlink em
        // atencao ja conta como entregue, mas tambem aparece em
        // "em atencao". A-001: o chip "em atencao" e' OR sobre
        // subestados (qualquer sinal amarelo).
        // ``system_error`` compartilha o contador "critico" com
        // ``critical`` — para o usuario, ambos sao "preciso intervir".
        // Pintura distinta vive na cor do pip/swatch, nao no contador.
        //
        // Os tiles do ``ModelingProgressBandV3`` derivam a aglutinacao
        // 9 -> 4 estados SEPARADAMENTE (via ``tallyStates(states)`` no
        // proprio componente da band) — entao aqui mantemos os
        // contadores granulares (winner-take-all por estado canonico)
        // sem aglutinar. Isso permite que ``delivered_tooltip`` e
        // outros consumidores continuem reportando a decomposicao
        // correta sem perder o sinal de cada estado.
        if (state === "critical" || state === "system_error") critical += 1;
        else if (state === "pending_decision") pendingDecision += 1;
        else if (state === "in_progress") inProgress += 1;
        else if (state === "not_sent") notSent += 1;
        if (hasAttentionSignal(inp)) attention += 1;
      }
    }
    return {
      total,
      delivered,
      deliveredClean,
      inProgress,
      pendingDecision,
      critical,
      attention,
      notSent,
      states,
    };
  };
  const handleBadgeClick = (scope, type, count) => {
    if (count === 0) return;
    if (
      badgeFilter &&
      badgeFilter.scope === scope &&
      badgeFilter.type === type
    ) {
      setBadgeFilter(null);
    } else {
      setBadgeFilter({ scope, type });
    }
  };
  // PA-019: ``renderCounterCard`` foi migrado para o sub-componente
  // ``CountCard`` dentro de ``ModelingProgressBandV3``.
  // Id "virtual" usado para representar inputs sem nenhum responsável
  // atribuído — aparece como linha na lista e é filtrável igual aos
  // outros.
  const NO_RESPONSIBLE_ID = "__no_responsible__";

  // Agrega contagens por responsável, cobrindo os 4 estados (entregue,
  // crítico, em atenção, não enviado). Inputs permanentes do mês são
  // excluídos (seguem a mesma regra dos contadores do topo).
  //
  // Multi-responsável: um input com N responsáveis contribui +1 em
  // cada um dos N — cada pessoa precisa agir independentemente. Se não
  // tiver responsável, entra em uma linha virtual "Sem responsável".
  const computeStatusByResponsible = () => {
    const map = new Map();
    const ensure = (id, name, hasResponsible, isCurrentUser) => {
      if (!map.has(id)) {
        map.set(id, {
          id,
          name,
          hasResponsible,
          isCurrentUser: !!isCurrentUser,
          critical: 0,
          attention: 0,
          inProgress: 0,
          notSent: 0,
          delivered: 0,
        });
      } else if (isCurrentUser) {
        // se aparecer o flag depois, promove
        map.get(id).isCurrentUser = true;
      }
      return map.get(id);
    };
    for (const group of currentInputsStatus) {
      for (const inp of group.inputs || []) {
        // Permanentes entram no contador (como delivered) para manter
        // paridade com a grade — antes eram ocultados do total por
        // responsavel, mas apareciam com o mineDot nos cards.
        const state = classifyInputState(inp);
        const respList = Array.isArray(inp.responsible) ? inp.responsible : [];
        const targets =
          respList.length > 0
            ? respList.map((r) => ({
                id: r.id,
                name: r.name,
                hasResp: true,
                isCurrentUser: !!r.is_current_user,
              }))
            : [
                {
                  id: NO_RESPONSIBLE_ID,
                  name: t("inputs_v3.stats.no_responsible"),
                  hasResp: false,
                  isCurrentUser: false,
                },
              ];
        for (const tgt of targets) {
          const entry = ensure(
            tgt.id,
            tgt.name,
            tgt.hasResp,
            tgt.isCurrentUser,
          );
          // PA-010 (rev. 2026-04-30): "entregue" agora segue a regra
          // canonica em ``isInputDelivered`` — parsing concluiu sem
          // erros E nenhum xlink critico/pergunta. Um input em
          // ``attention`` (xlink warning) ja conta como entregue E
          // tambem aparece no chip "em atencao".
          // ``system_error`` agrupa com ``critical`` no contador
          // por responsavel — mesma justificativa de ``computeCounters``.
          if (state === "critical" || state === "system_error")
            entry.critical += 1;
          else if (state === "attention") entry.attention += 1;
          else if (state === "in_progress") entry.inProgress += 1;
          else if (state === "not_sent") entry.notSent += 1;
          if (isInputDelivered(inp)) entry.delivered += 1;
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      // Usuario visualizador sempre em primeiro, independente do numero
      // de pendencias (feature solicitada).
      if (a.isCurrentUser !== b.isCurrentUser) return a.isCurrentUser ? -1 : 1;
      const pa = a.critical + a.attention + a.notSent;
      const pb = b.critical + b.attention + b.notSent;
      if (pa !== pb) return pb - pa;
      return (
        b.critical - a.critical ||
        b.attention - a.attention ||
        b.notSent - a.notSent ||
        a.name.localeCompare(b.name)
      );
    });
  };

  // Toggle de um responsável no filtro — igual ao dropdown legado,
  // mas disparado por clique na linha / checkbox.
  const toggleResponsibleFilter = (id, name) => {
    setFilteredResponsibleUsers((prev) => {
      const exists = prev.some((u) => u.id === id);
      if (exists) return prev.filter((u) => u.id !== id);
      return [...prev, { id, name }];
    });
  };

  // PA-019: ``renderTinyBadge``, ``renderTinyCheckbox``,
  // ``renderResponsibleCard`` e ``topCountersRow`` viviam aqui e
  // foram migrados para ``ModelingProgressBandV3``.
  // endregion
  // endregion

  return (
    <div>
      {/* Boot da view: enquanto ``monthlyStatus`` nao chegar, o
          conteudo da pagina e completamente suprimido e exibimos o
          ``FullPageLoader`` — mesmo padrao usado pelo ``PLExplorer``
          para o boot da view de resultados (spinner Grid, texto
          "preparando seus dados", logo do cliente, fundo solido).
          Antes, o cabecalho do card de inputs ficava visivel com um
          ThreeDots minusculo dentro do CardBody, dando a impressao
          que a view ja estava pronta e so faltava algum detalhe. */}
      <FullPageLoader isVisible={monthlyStatus.length === 0} />
      <GridContainer style={{ marginTop: "-40px", marginBottom: "25px" }}>
        <GridItem xs={12}>
          <div
            className={cx({
              [classes.inputsCardWrapper]: true,
              ["loading"]: currentInputsStatus.length === 0,
            })}
          >
            <Card
              style={{
                minWidth: "1200px",
                marginBottom: "0",
                position: "relative",
              }}
            >
              <CardHeader color="client" icon>
                <CardIcon color="client">
                  <Assignment />
                </CardIcon>
                <h4 className={classes.cardIconTitle}>
                  {t("modeling_inputs_v2.inputs_list")}
                </h4>
                {(!isRefreshing || monthlyStatus.length > 0) && (
                  <div className={classes.viewButtonsContainer}>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => setHowToModalOpen(true)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          setHowToModalOpen(true);
                        }
                      }}
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                        marginTop: "1px",
                        color: grayColor[1],
                        fontSize: "11px",
                        cursor: "pointer",
                      }}
                    >
                      {t("inputs_v3.howto.trigger")}
                    </span>
                    <span
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                        marginTop: "1px",
                        margin: "0 8px",
                        color: grayColor[1],
                        fontSize: "11px",
                      }}
                    >
                      |
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => setDataPrivacyModalOpen(true)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          setDataPrivacyModalOpen(true);
                        }
                      }}
                      style={{
                        display: "inline-block",
                        verticalAlign: "middle",
                        marginTop: "1px",
                        marginRight: "16px",
                        color: grayColor[1],
                        fontSize: "11px",
                        cursor: "pointer",
                      }}
                    >
                      {t("modeling_inputs_v3.data_privacy.trigger")}
                    </span>
                    <div
                      style={{
                        position: "relative",
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                    >
                      <Button
                        size={"sm"}
                        color={"client"}
                        simple={currentView !== "detailed"}
                        disabled={monthlyStatus.length === 0}
                        className={cx({
                          [classes.viewButton]: true,
                          [classes.firstButton]: true,
                          [classes.buttonSelected]: currentView === "detailed",
                        })}
                        onClick={() => setCurrentView("detailed")}
                      >
                        {t("modeling_inputs_v2.detailed_view")}
                      </Button>
                      <Button
                        size={"sm"}
                        color={"client"}
                        simple={currentView !== "general"}
                        disabled={monthlyStatus.length === 0}
                        className={cx({
                          [classes.viewButton]: true,
                          [classes.lastButton]: true,
                          [classes.buttonSelected]: currentView === "general",
                        })}
                        onClick={() => setCurrentView("general")}
                      >
                        {t("modeling_inputs_v2.overview")}
                      </Button>
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardBody style={{ padding: "0 10px 10px" }}>
                <div
                  style={{
                    marginTop: "8px",
                    width: "100%",
                    padding: "0",
                  }}
                >
                  {/* O ThreeDots inline que existia aqui para o estado
                      ``monthlyStatus.length === 0`` foi removido — esse
                      caso agora suprime o card inteiro e renderiza o
                      ``lowerSpinner`` centralizado na viewport (vide
                      bloco logo acima do wrapper). */}
                  {currentView === "detailed" &&
                    monthlyStatus.length > 0 &&
                    monthSelectionTabs()}
                </div>
                {currentView === "detailed" &&
                  monthlyStatus.length > 0 &&
                  selectedMonthData &&
                  monthlyStatusDiv()}
                {currentView === "detailed" &&
                  monthlyStatus.length > 0 &&
                  currentInputsStatus.length > 0 && (
                    <ModelingProgressBandV3
                      embedded
                      allCounters={computeCounters()}
                      mineCounters={computeCounters((inp) => inp.is_mine)}
                      responsibleList={computeStatusByResponsible()}
                      badgeFilter={badgeFilter}
                      onBadgeClick={handleBadgeClick}
                      filteredResponsibleUsers={filteredResponsibleUsers}
                      onToggleResponsible={toggleResponsibleFilter}
                      monthLabel={modelingProgressBandMonthLabel}
                      transitionFaded={isRefreshFadeActive}
                    />
                  )}
              </CardBody>
            </Card>
            {maintenanceStatus?.is_active && (
              <div style={{ marginTop: "8px" }}>
                <SnackbarContent
                  message={
                    <span>{t("inputs_v3.maintenance.page_banner")}</span>
                  }
                  color="warning"
                  paperClass={classes.batchWarningSnackbar}
                />
              </div>
            )}
            {/* PA-016 (2026-05-03): durante a troca de mes a lista
                permanece visivel (dados do mes anterior) com
                opacidade levemente reduzida — sinal de "estou
                buscando o novo mes" sem layout shift, sem zerar
                contadores e sem o spinner se mover. O ``transition``
                no wrapper suaviza a entrada/saida do dim em 200ms. */}
            {currentView === "detailed" &&
              monthlyStatus.length > 0 &&
              currentInputsStatus.length > 0 && (
                <div
                  style={{
                    opacity: isRefreshFadeActive ? 0.45 : 1,
                    transition: "opacity 180ms ease",
                    pointerEvents: isRefreshFadeActive ? "none" : "auto",
                  }}
                >
                  {sortToggleBar()}
                  {inputsComponent()}
                </div>
              )}
            {/* Batch operation warning */}
            {currentView === "detailed" &&
              specialOptionsApi.activeBatchJob &&
              specialOptionsApi.isMonthInBatchRange(selectedMonthData) && (
                <div style={{ marginTop: "8px" }}>
                  <SnackbarContent
                    message={
                      <span>
                        <strong>
                          {specialOptionsApi.activeBatchJob.operation_type ===
                          "RECALCULATE"
                            ? t(
                                "modeling_inputs_v2.batch_operations.warning_title_recalculate",
                              )
                            : t(
                                "modeling_inputs_v2.batch_operations.warning_title_reset",
                              )}
                        </strong>
                        <span style={{ display: "block", marginTop: "4px" }}>
                          {t(
                            "modeling_inputs_v2.batch_operations.warning_message",
                          )}
                        </span>
                      </span>
                    }
                    color="warning"
                    paperClass={classes.batchWarningSnackbar}
                  />
                </div>
              )}
            {currentView === "general" &&
              monthlyStatus.length > 0 &&
              currentInputsStatus.length > 0 && (
                <div
                  style={{
                    opacity: isRefreshFadeActive ? 0.55 : 1,
                    transition: "opacity 180ms ease",
                    pointerEvents: isRefreshFadeActive ? "none" : "auto",
                  }}
                >
                  <Card style={{ marginTop: "8px", marginBottom: "0" }}>
                    <CardBody style={{ padding: "10px" }}>
                      <OverviewV3
                        inputsStatus={currentInputsStatus}
                        monthlyStatus={monthlyStatusV3}
                        selectedMonth={selectedMonth}
                        onOpenInput={handleOverviewOpenInput}
                        onOpenMonthProgress={(idx) => {
                          // Click no status do mes (linha 0 da
                          // tabela do OverviewV3) — sincroniza o
                          // seletor de mes da pagina com o mes
                          // clicado e abre o ProgressDrawerV3 ja
                          // apontando para ele.
                          selectMonthIndex(idx);
                          setProgressModalOpen(true);
                        }}
                        selectStyles={selectStyles}
                        inputsStateByPeriod={
                          progressOverview.data?.inputs_state_by_period
                        }
                      />
                    </CardBody>
                  </Card>
                </div>
              )}
          </div>
          {/* O ``lowerSpinner`` agora aparece SO no boot da view
              (``inputsStatus.length === 0``) — durante a troca de
              mes a lista anterior fica visivel (com dim) ate que os
              novos dados cheguem. Esse caminho cobre apenas a
              primeira carga: assim que ha dados, ``inputsStatus``
              fica nao-vazio para sempre. */}
          {(currentView === "detailed" || currentView === "general") &&
            currentInputsStatus.length === 0 &&
            monthlyStatus.length > 0 &&
            lowerSpinner}
        </GridItem>
      </GridContainer>
      <InputDrawerV3
        open={inputDrawerInput !== null}
        input={currentInputDrawerInput}
        appName={v3AppNameRef.current}
        year={selectedMonthData?.year}
        month={selectedMonthData?.month}
        period={(() => {
          if (!selectedMonthData) return "";
          // Nome completo do mês ("Junho", "Dezembro", ...) via chave
          // `common.months.<abbr>` do translation.json — a chave
          // `months.<n>` no topo do dicionário traz apenas abreviações
          // em minúsculo ("jun"), que não atendem ao visual do header.
          const MONTH_ABBR = [
            "jan",
            "feb",
            "mar",
            "apr",
            "may",
            "jun",
            "jul",
            "aug",
            "sep",
            "oct",
            "nov",
            "dec",
          ];
          const monthLabel = t(
            `common.months.${MONTH_ABBR[selectedMonthData.month - 1]}`,
          );
          return `${monthLabel} · ${selectedMonthData.year}`;
        })()}
        isUploading={isUploading}
        busyAction={busyAction}
        isDownloading={isDownloadingFile}
        onClose={() => {
          if (isUploading) return;
          setInputDrawerInput(null);
        }}
        onUpload={handleUploadFileClick}
        onReplicate={handleReplicateFileClick}
        onReprocess={handleReprocessFileClick}
        onDownload={handleDownloadInputDrawer}
        onFileDrop={
          canUploadInputs
            ? (file) => handleInputFileDrop(inputDrawerInput, file)
            : undefined
        }
        onDropReject={handleDropReject}
        // FONTE UNICA DE VERDADE (onda 1.9 — mai/2026):
        // ``onRuntimeChange`` foi REMOVIDO. Antes, o drawer empurrava
        // seu proprio ``runtimeInput`` (oriundo do polling 5s de
        // ``/drawer-details/``) de volta para o ``inputsStatus`` do
        // cockpit (polling 4s de ``/snapshot/`` + ``/progress_overview/``)
        // via ``syncInputRuntimeFromDrawer``. Resultado: quando o
        // drawer estava UM TICK atras do cockpit, ele REGREDIA o cockpit
        // para o seu estado mais antigo — usuario via custodia_koner
        // cinza shimmer enquanto a UI deveria ja mostrar xlink rodando.
        // Fluxo correto: cockpit e' a fonte unica para status do input;
        // drawer apenas LE (panes consomem ``drawerDetails`` para notes/
        // crossLinks/decisions/audit, e o header consome ``inputDrawer
        // Input`` que ja' e' sincronizado com ``inputsStatus``).
        fileInputRef={fileInputRef}
        monthlyStatus={monthlyStatusV3}
        selectedMonthIndex={
          typeof selectedMonth === "number" ? selectedMonth : null
        }
        onSelectMonth={(idx) => {
          selectMonthIndex(idx);
        }}
        usersList={usersRef.current}
        userRole={userDetails?.user_role}
        onSaveResponsibles={handleSaveResponsibles}
        isSavingResponsibles={isSavingResponsibles}
        selectStyles={selectStyles}
        drawerDetailsOverride={drawerDetailsOverride}
        onMarkNoteVerified={handleMarkNoteVerifiedV3}
      />
      <UploadQueueDrawer
        classes={classes}
        selectStyles={selectStyles}
        getDate={getDate}
        selectedMonthData={selectedMonthData}
        inputsStatus={currentInputsStatus}
        queueDrawerOpen={queueDrawerOpen}
        setQueueDrawerOpen={setQueueDrawerOpen}
        isQueueActive={isQueueActive}
        setQueueRunning={setQueueRunning}
        canRunQueue={canRunQueue}
        uploadQueueItems={uploadQueueItems}
        setQueuePauseAfterCurrent={setQueuePauseAfterCurrent}
        queueIgnoreUploaded={queueIgnoreUploaded}
        setQueueIgnoreUploaded={setQueueIgnoreUploaded}
        clearQueue={clearQueue}
        queueFolderInputRef={queueFolderInputRef}
        handleQueueFolderSelected={handleQueueFolderSelected}
        queueSelectResetKey={queueSelectResetKey}
        queueNewSelectedInputId={queueNewSelectedInputId}
        setQueueNewSelectedInputId={setQueueNewSelectedInputId}
        handleQueuePickFile={handleQueuePickFile}
        queueFileInputRef={queueFileInputRef}
        queuePendingInputIdRef={queuePendingInputIdRef}
        addItemToQueue={addItemToQueue}
        setQueueSelectResetKey={setQueueSelectResetKey}
        moveItemUp={moveItemUp}
        moveItemDown={moveItemDown}
        removeItemFromQueue={removeItemFromQueue}
      />
      {inputDrawerInput && (
        <InputNotesDialog
          open={displayNotesModalOpen}
          onClose={() => setDisplayNotesModalOpen(false)}
          input={inputDrawerInput}
          verifyingNoteUuids={inputControlsMarkedAsVerifiedLine}
          onMarkAsAnalyzed={handleMarkAsAnalyzedClick}
          onValuesCopied={notifyCopy}
          classes={classes}
          Transition={Transition}
        />
      )}
      <ResetInputsDialog
        open={resetInputsModalOpen}
        onClose={() => setResetInputsModalOpen(false)}
        onConfirm={handleConfirmResetInput}
        disableActions={currentInputsStatus.length === 0}
        classes={classes}
        Transition={Transition}
      />
      <DataPrivacyModal
        open={dataPrivacyModalOpen}
        onClose={() => setDataPrivacyModalOpen(false)}
        classes={classes}
        Transition={Transition}
      />
      <HowToModalV3
        open={howToModalOpen}
        onClose={() => setHowToModalOpen(false)}
        Transition={Transition}
      />
      <SpecialOptionsModal
        api={specialOptionsApi}
        classes={classes}
        Transition={Transition}
        selectStyles={selectStyles}
        userDetails={userDetails}
        clientColor={clientColor}
      />
      {/*
        ProgressDrawerV3 (sub-leva A) substituiu o ``ProgressDashboardModal``
        antigo. O drawer agora possui seletor de mes proprio no header
        (``onSelectMonth`` -> ``setSelectedMonth`` + ``setSelectedMonthData``),
        cockpit fixo de KPIs/progresso e 3 panes (Timeline / Pendencias /
        Calculos). Conteudo das panes ainda e mock — sera plugado a backend
        em sub-levas seguintes (B: pendencias+timeline; C: blocos+logs).

        TO-DO (sub-leva A): replicar a logica de seletor de mes do header
        deste drawer dentro do ``InputDrawerV3`` (drawer do input). Hoje o
        input drawer recebe ``period`` como string fixa; o usuario deveria
        poder navegar entre meses sem ter que fechar o drawer.
      */}
      {/*
        Cockpit do drawer recebe os contadores reais de "Inputs enviados"
        (computeCounters() respeita a logica de classifyInputState +
        COUNTS_AS_DELIVERED ja existente). Os demais KPIs continuam mock
        ate a sub-leva B/C — cada um tera sua origem dedicada:
          * Meses da implementacao  → /api/implementations/{id}/months_progress
          * Acoes necessarias      → /api/.../pendencies (sub-leva B)
          * Calculos na fila       → /api/.../calculation_queue
        Repassamos ``undefined`` para os mocks remanescentes para que
        ``CockpitStrip.defaultProps`` continue valendo.
      */}
      {(() => {
        const c = computeCounters();
        const inputsSentReal = {
          sent: c.delivered,
          action: c.critical + c.pendingDecision,
          waiting: c.notSent,
          total: c.total,
        };
        return (
          <ProgressDrawerV3
            open={progressModalOpen}
            onClose={() => setProgressModalOpen(false)}
            initialTab={progressInitialTab}
            monthlyStatus={monthlyStatusV3}
            selectedMonthIndex={
              typeof selectedMonth === "number" ? selectedMonth : -1
            }
            onSelectMonth={selectMonthIndex}
            inputsSent={c.total > 0 ? inputsSentReal : undefined}
            progressOverview={progressOverview.data}
            progressOverviewLoading={progressOverview.loading}
            onOpenInput={handleProgressLogOpenInput}
            appName={v3AppNameRef.current}
            userRole={userDetails?.user_role}
            onMonthReset={refreshInputsStatus}
            blocksOverride={inputsState.data?.blocks || null}
          />
        );
      })()}
    </div>
  );
}
